import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getUserPageLevel, readStoredUser, UNAUTHORIZED_LABEL } from "../utils/permissions";
import { firstValidationKey, validateBookingRegistry, validateCustomerRegistry } from "../utils/registryValidation";
import { getCustomerRegistryType, getCustomerRegistryTypeFromCustomer, normalizeCustomerGender } from "../utils/customerRegistryTypes";
import { formatVestaDate } from "../utils/dateInput";
import { confirmBooking, createBooking, createBookingAccountService, createCustomer, createRoom, createRoomCleaning, createRoomMaintenance, deleteBooking, deleteBookingAccountService, deleteRoomCleaning, deleteRoomMaintenance, getBookingAccounts, getBookings, getLastBookingGuests, getCatalogItems, getCreditCardCircuits, getCustomers, getRooms, getServiceCatalog, getSites, issueBookingCheckoutDocument, updateBooking, updateBookingAccountService, updateBookingPlanning, updateCustomer, updateRoom, updateRoomCleaning, updateRoomMaintenance } from "../api";
import {
  EMPTY_BOOKING,
  EMPTY_CUSTOMER,
  EMPTY_ROOM_DRAFT,
  CHECKIN_DEFAULTS,
  MONTHS,
  ROOM_STATUS_FALLBACK,
  STATUS_FILTER_OPTIONS,
  STATUS_LABELS,
  STATUS_ALL_DOT_ORDER,
  WEEKDAYS,
  addDays,
  addMonths,
  bookingToDraft,
  formatDateForInput,
  formatMoneyFieldValue,
  formatRangeMonth,
  getBookingId,
  getBookingName,
  getRoomGroups,
  isCancelledStatus,
  money,
  normalizeDateFieldValue,
  normalizeMoneyToNumber,
  normalizeMonthFieldValue,
  normalizeBookingPayments,
  deriveBookingOccupancyCounts,
  normalizeStatus,
  parseFlexibleDate,
  startOfDay,
  toCheckInDateTimeFromInput,
  toCheckOutDateTimeFromInput,
  toIsoDate,
  toIsoDateFromInput,
  valueToDateTimeInput,
  valueToDateInput,
  BookingOverlay,
} from "../components/bookings/bookingPlanningParts";
import { CustomerOverlay, normalizeCustomerDraft as normalizeSharedCustomerDraft, normalizeCustomerForSave } from "./Customers";
import { contactPreferenceDefaults } from "../components/common/CustomerRegistryLists";
import VestaSelect from "../components/common/VestaSelect";
import { BookingCheckoutOverlay, BookingServiceOverlay, EMPTY_CHECKOUT_DRAFT, buildCheckoutDraft, fetchFreshBookingAccount, mergeServiceDraftIntoBookingAccount, todayIso } from "./BookingAccounts";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";

const FOREIGN_PRIVATE_TAX_ID = "0000000";

function fastStayNights(checkIn, checkOut) {
  const start = checkIn ? new Date(checkIn) : null;
  const end = checkOut ? new Date(checkOut) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
}

function maintenanceDatePart(value) {
  const normalized = valueToDateTimeInput(value);
  return normalized ? normalized.slice(0, 10) : "";
}

function maintenanceTimePart(value) {
  const normalized = valueToDateTimeInput(value);
  return normalized ? normalized.slice(11, 16) : "";
}

function maintenanceDateTimeValue(dateValue, timeValue) {
  const date = String(dateValue || "").trim();
  const time = String(timeValue || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return "";
  return `${date}T${time}`;
}

function planningDateValue(value) {
  const normalized = valueToDateTimeInput(value);
  if (!normalized) return null;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildFastServiceDraft(booking = {}) {
  const nights = Math.max(1, Number(booking.stay_nights || fastStayNights(booking.check_in, booking.check_out) || 1));
  return {
    stay: {
      chargeDate: booking.room_charge_date ? String(booking.room_charge_date).slice(0, 10) : String(booking.check_in || "").slice(0, 10),
      quantity: nights,
      unitPrice: Number(booking.room_rate ?? (Number((booking.room_net_total ?? booking.booking_total) || 0) / nights)),
      discountRate: Number(booking.room_discount_rate || 0),
      vatRate: Number(booking.room_vat_rate || 0),
      entrySource: "AUTOMATIC",
      note: booking.room_note || "",
    },
    rows: (booking.services || []).map((service) => ({
      _id: service._id,
      service_id: service.service_id || "",
      description: service.description || "",
      chargeType: service.chargeType || "FORFAIT",
      entrySource: service.entrySource || "MANUAL_DESKTOP",
      accumulationItems: Array.isArray(service.accumulationItems) ? service.accumulationItems : [],
      quantity: Number(service.quantity || 1),
      unitPrice: Number(service.unitPrice || 0),
      discountRate: Number(service.discountRate || 0),
      vatRate: Number(service.vatRate || 0),
      chargeDate: service.chargeDate ? String(service.chargeDate).slice(0, 10) : todayIso(),
      note: service.note || "",
      isNew: false,
    })),
    removedIds: [],
  };
}

function cleanCustomerText(value) {
  return String(value || "").trim();
}

function customerDraftValue(source = {}, legacyField, canonicalField, fallback = "") {
  if (Object.prototype.hasOwnProperty.call(source, legacyField)) return source[legacyField] ?? fallback;
  if (Object.prototype.hasOwnProperty.call(source, canonicalField)) return source[canonicalField] ?? fallback;
  return fallback;
}

const CUSTOMER_DRAFT_TWIN_FIELDS = {
  nazione: "country",
  codiceFiscale: "fiscalCode",
  partitaIva: "vatNumber",
  codiceSdi: "sdiCode",
  cognome: "surname",
  nome: "name",
  ragioneSociale: "businessName",
  indirizzo: "address",
  civico: "streetNumber",
  citta: "city",
  provincia: "province",
  cap: "postalCode",
  dataNascita: "birthDate",
  luogoNascita: "birthPlace",
  comuneNascita: "birthCity",
  provinciaNascita: "birthProvince",
  statoNascita: "birthCountry",
  cittadinanza: "nationality",
  sesso: "gender",
  linguaPreferita: "preferredLanguage",
  pianoPreferito: "preferredFloor",
  allergie: "allergies",
  intolleranze: "intolerances",
  esigenzeSpeciali: "specialNeeds",
  preferenzaArrivo: "arrivalPreference",
  note: "notes",
};
const CUSTOMER_DRAFT_TWIN_FIELDS_REVERSE = Object.fromEntries(Object.entries(CUSTOMER_DRAFT_TWIN_FIELDS).map(([legacy, canonical]) => [canonical, legacy]));

function normalizeCustomerContactType(value) {
  const text = cleanCustomerText(value);
  const token = text.toLowerCase();
  if (["telefono", "tel", "fisso"].includes(token)) return "Telefono";
  if (["cellulare", "mobile", "cell", "whatsapp"].includes(token)) return "Cellulare";
  if (["email", "mail", "e-mail"].includes(token)) return "Email";
  if (token === "pec") return "PEC";
  if (token === "fax") return "Fax";
  return text;
}

function normalizeCustomerContacts(source = {}) {
  const contacts = Array.isArray(source.contacts) ? source.contacts.map((contact) => {
    const tipoContatto = normalizeCustomerContactType(contact?.tipoContatto || contact?.contactType || contact?.type || contact?.tipo || "");
    const datoContatto = cleanCustomerText(contact?.datoContatto || contact?.contactValue || contact?.value || contact?.dato || "");
    const interlocutore = cleanCustomerText(contact?.interlocutore || contact?.interlocutor || contact?.referente || "");
    const countryPrefixId = cleanCustomerText(contact?.countryPrefixId || contact?.prefixId || contact?.prefissoId || "");
    const countryPrefixDescription = cleanCustomerText(contact?.countryPrefixDescription || "");
    const countryPrefixValue = cleanCustomerText(contact?.countryPrefixValue || contact?.prefix || "");
    const primary = contact?.primary === true || contact?.principale === true;
    return {
      tipoContatto,
      contactType: tipoContatto,
      datoContatto,
      contactValue: datoContatto,
      interlocutore,
      interlocutor: interlocutore,
      countryPrefixId,
      countryPrefixDescription,
      countryPrefixValue,
      primary,
    };
  }).filter((contact) => contact.tipoContatto || contact.datoContatto || contact.interlocutore || contact.countryPrefixId || contact.primary) : [];

  if (!Array.isArray(source.contacts)) [["telefono", "Telefono"], ["cellulare", "Cellulare"], ["email", "Email"], ["pec", "PEC"], ["fax", "Fax"]].forEach(([field, tipoContatto]) => {
    const datoContatto = cleanCustomerText(source[field]);
    if (datoContatto && !contacts.some((contact) => contact.tipoContatto === tipoContatto && contact.datoContatto === datoContatto)) {
      contacts.push({
        tipoContatto,
        contactType: tipoContatto,
        datoContatto,
        contactValue: datoContatto,
        interlocutore: "",
        interlocutor: "",
        countryPrefixId: "",
        countryPrefixDescription: "",
        countryPrefixValue: "",
        primary: false,
      });
    }
  });
  return contacts;
}

function customerContactValue(source = {}, types = []) {
  const wanted = types.map((type) => String(type || "").toLowerCase());
  return normalizeCustomerContacts(source).find((contact) => wanted.includes(String(contact.tipoContatto || "").toLowerCase()))?.datoContatto || "";
}

function customerPrimaryContactValue(source = {}, type = "") {
  const wanted = String(type || "").toLowerCase();
  const matches = normalizeCustomerContacts(source).filter((contact) => String(contact.tipoContatto || "").toLowerCase() === wanted);
  return matches.find((contact) => contact.primary === true)?.datoContatto || "";
}

function customerPhoneMobileValue(source = {}) {
  const phone = cleanCustomerText(customerPrimaryContactValue(source, "Telefono"));
  const mobile = cleanCustomerText(customerPrimaryContactValue(source, "Cellulare"));
  if (phone && mobile) return `${phone} - ${mobile}`;
  return phone || mobile || "";
}

function customerDisplayName(customer = {}) {
  return cleanCustomerText(customer.businessName || customer.ragioneSociale)
    || [customer.surname || customer.cognome, customer.name || customer.nome].map(cleanCustomerText).filter(Boolean).join(" ")
    || cleanCustomerText(customer.guest_name || customer.customerName);
}

function findCustomerById(customers = [], customerId = "") {
  const id = cleanCustomerText(customerId);
  if (!id) return null;
  return (customers || []).find((customer) => String(customer._id || customer.id || "") === id) || null;
}

function normalizeCustomerDocuments(source = {}) {
  const rows = Array.isArray(source.documents) ? source.documents : [];
  const documents = rows.map((document) => ({
    documentType: cleanCustomerText(document?.documentType || document?.documentoTipo || document?.type || document?.tipo),
    documentNumber: cleanCustomerText(document?.documentNumber || document?.documentoNumero || document?.number || document?.numero),
    documentIssuePlace: cleanCustomerText(document?.documentIssuePlace || document?.luogoRilascioDocumento || document?.issuePlace),
    documentIssueCountry: cleanCustomerText(document?.documentIssueCountry || document?.paeseRilascioDocumento || document?.issueCountry),
    documentIssueDate: cleanCustomerText(document?.documentIssueDate || document?.dataRilascioDocumento || document?.issueDate),
    documentExpiryDate: cleanCustomerText(document?.documentExpiryDate || document?.dataScadenzaDocumento || document?.expiryDate),
    documentIssuer: cleanCustomerText(document?.documentIssuer || document?.enteRilascioDocumento || document?.issuer),
    documentIsPrimary: document?.documentIsPrimary === true || document?.documentoPrincipale === true,
  })).filter((document) => document.documentType || document.documentNumber || document.documentIssuePlace || document.documentIssueCountry || document.documentIssueDate || document.documentExpiryDate || document.documentIssuer);
  const legacyDocument = {
    documentType: cleanCustomerText(source.documentoTipo || source.documentType),
    documentNumber: cleanCustomerText(source.documentoNumero || source.documentNumber),
    documentIssuePlace: cleanCustomerText(source.luogoRilascioDocumento || source.documentIssuePlace),
    documentIssueCountry: cleanCustomerText(source.paeseRilascioDocumento || source.documentIssueCountry),
    documentIssueDate: cleanCustomerText(source.dataRilascioDocumento || source.documentIssueDate),
    documentExpiryDate: cleanCustomerText(source.dataScadenzaDocumento || source.documentExpiryDate),
    documentIssuer: cleanCustomerText(source.enteRilascioDocumento || source.documentIssuer),
    documentIsPrimary: source.documentoPrincipale === true || source.documentIsPrimary === true,
  };
  if (Object.values(legacyDocument).some(Boolean)
    && !documents.some((document) => document.documentType === legacyDocument.documentType && document.documentNumber === legacyDocument.documentNumber)) {
    documents.push(legacyDocument);
  }
  return documents;
}

function normalizeCustomerBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  const token = cleanCustomerText(value).toLowerCase();
  if (["true", "1", "s", "si", "sì", "yes"].includes(token)) return true;
  if (["false", "0", "n", "no"].includes(token)) return false;
  return fallback;
}

function normalizeCustomerTags(value) {
  const source = Array.isArray(value) ? value : cleanCustomerText(value).split(/[;,]/g);
  return [...new Set(source.map(cleanCustomerText).filter(Boolean))];
}

function customerTagsText(value) {
  return normalizeCustomerTags(value).join(", ");
}

function normalizeExternalReferences(value = []) {
  if (Array.isArray(value)) {
    return value.map((item) => ({
      source: cleanCustomerText(item?.source || item?.origine || item?.provider),
      externalId: cleanCustomerText(item?.externalId || item?.idEsterno || item?.reference),
    })).filter((item) => item.source || item.externalId);
  }
  return cleanCustomerText(value).split(";").map((part) => {
    const pieces = part.split("|").map(cleanCustomerText);
    return { source: pieces[0] || "", externalId: pieces[1] || "" };
  }).filter((item) => item.source || item.externalId);
}

function customerRegistryDocumentsForSave(source = {}) {
  if (Array.isArray(source.documents)) {
    return normalizeCustomerDocuments({ documents: source.documents });
  }

  return normalizeCustomerDocuments({
    documentoTipo: source.documentoTipo,
    documentoNumero: source.documentoNumero,
    luogoRilascioDocumento: source.luogoRilascioDocumento,
    paeseRilascioDocumento: source.paeseRilascioDocumento,
    dataRilascioDocumento: source.dataRilascioDocumento,
    dataScadenzaDocumento: source.dataScadenzaDocumento,
    enteRilascioDocumento: source.enteRilascioDocumento,
    documentoPrincipale: source.documentoPrincipale,
  });
}

function firstCustomerDocument(source = {}) {
  return normalizeCustomerDocuments(source)[0] || {};
}

function normalizeCustomerDraft(source = {}) {
  const firstDocument = firstCustomerDocument(source);
  const nazione = cleanCustomerText(customerDraftValue(source, "nazione", "country", "Italia")) || "Italia";
  const codiceFiscale = cleanCustomerText(customerDraftValue(source, "codiceFiscale", "fiscalCode"));
  const partitaIva = cleanCustomerText(customerDraftValue(source, "partitaIva", "vatNumber"));
  const codiceSdi = cleanCustomerText(customerDraftValue(source, "codiceSdi", "sdiCode"));
  const cognome = cleanCustomerText(customerDraftValue(source, "cognome", "surname"));
  const nome = cleanCustomerText(customerDraftValue(source, "nome", "name"));
  const ragioneSociale = cleanCustomerText(customerDraftValue(source, "ragioneSociale", "businessName"));
  const indirizzo = cleanCustomerText(customerDraftValue(source, "indirizzo", "address"));
  const civico = cleanCustomerText(customerDraftValue(source, "civico", "streetNumber"));
  const citta = cleanCustomerText(customerDraftValue(source, "citta", "city"));
  const provincia = cleanCustomerText(customerDraftValue(source, "provincia", "province"));
  const cap = cleanCustomerText(customerDraftValue(source, "cap", "postalCode"));
  const dataNascita = cleanCustomerText(customerDraftValue(source, "dataNascita", "birthDate"));
  const luogoNascita = cleanCustomerText(customerDraftValue(source, "luogoNascita", "birthPlace"));
  const comuneNascita = cleanCustomerText(customerDraftValue(source, "comuneNascita", "birthCity"));
  const provinciaNascita = cleanCustomerText(customerDraftValue(source, "provinciaNascita", "birthProvince"));
  const statoNascita = cleanCustomerText(customerDraftValue(source, "statoNascita", "birthCountry", "Italia")) || "Italia";
  const cittadinanza = cleanCustomerText(customerDraftValue(source, "cittadinanza", "nationality", "Italia")) || "Italia";
  const sesso = normalizeCustomerGender(customerDraftValue(source, "sesso", "gender"));
  const linguaPreferita = cleanCustomerText(customerDraftValue(source, "linguaPreferita", "preferredLanguage"));
  const pianoPreferito = cleanCustomerText(customerDraftValue(source, "pianoPreferito", "preferredFloor"));
  const allergie = cleanCustomerText(customerDraftValue(source, "allergie", "allergies"));
  const intolleranze = cleanCustomerText(customerDraftValue(source, "intolleranze", "intolerances"));
  const esigenzeSpeciali = cleanCustomerText(customerDraftValue(source, "esigenzeSpeciali", "specialNeeds"));
  const preferenzaArrivo = cleanCustomerText(customerDraftValue(source, "preferenzaArrivo", "arrivalPreference"));
  const note = cleanCustomerText(customerDraftValue(source, "note", "notes"));
  return {
    ...EMPTY_CUSTOMER,
    ...source,
    nazione,
    country: nazione,
    codiceFiscale,
    fiscalCode: codiceFiscale,
    partitaIva,
    vatNumber: partitaIva,
    codiceSdi,
    sdiCode: codiceSdi,
    cognome,
    surname: cognome,
    nome,
    name: nome,
    ragioneSociale,
    businessName: ragioneSociale,
    indirizzo,
    address: indirizzo,
    civico,
    streetNumber: civico,
    citta,
    city: citta,
    provincia,
    province: provincia,
    cap,
    postalCode: cap,
    birthDate: dataNascita,
    dataNascita,
    birthPlace: luogoNascita,
    luogoNascita,
    birthCity: comuneNascita,
    comuneNascita,
    birthProvince: provinciaNascita,
    provinciaNascita,
    birthCountry: statoNascita,
    statoNascita,
    nationality: cittadinanza,
    cittadinanza,
    gender: sesso,
    sesso,
    documentoTipo: cleanCustomerText(source.documentoTipo || firstDocument.documentType),
    documentoNumero: cleanCustomerText(source.documentoNumero || firstDocument.documentNumber),
    luogoRilascioDocumento: cleanCustomerText(source.luogoRilascioDocumento || firstDocument.documentIssuePlace),
    paeseRilascioDocumento: cleanCustomerText(source.paeseRilascioDocumento || firstDocument.documentIssueCountry),
    dataRilascioDocumento: cleanCustomerText(source.dataRilascioDocumento || firstDocument.documentIssueDate),
    dataScadenzaDocumento: cleanCustomerText(source.dataScadenzaDocumento || firstDocument.documentExpiryDate),
    enteRilascioDocumento: cleanCustomerText(source.enteRilascioDocumento || firstDocument.documentIssuer),
    documentoPrincipale: source.documentoPrincipale === true || firstDocument.documentIsPrimary === true,
    preferredLanguage: linguaPreferita,
    linguaPreferita,
    preferredRoomTypeId: cleanCustomerText(source.preferredRoomTypeId || source.tipoCameraPreferitaId),
    preferredFloor: pianoPreferito,
    pianoPreferito,
    preferredBedTypeId: cleanCustomerText(source.preferredBedTypeId || source.tipoLettoPreferitoId),
    allergies: allergie,
    allergie,
    intolerances: intolleranze,
    intolleranze,
    specialNeeds: esigenzeSpeciali,
    esigenzeSpeciali,
    arrivalPreference: preferenzaArrivo,
    preferenzaArrivo,
    customerTags: customerTagsText(source.customerTags || source.tagsCliente),
    privacyConsent: normalizeCustomerBoolean(source.privacyConsent ?? source.consensoPrivacy, false),
    privacyConsentDate: cleanCustomerText(source.privacyConsentDate || source.dataConsensoPrivacy),
    marketingConsent: normalizeCustomerBoolean(source.marketingConsent ?? source.consensoMarketing, false),
    marketingConsentDate: cleanCustomerText(source.marketingConsentDate || source.dataConsensoMarketing),
    profilingConsent: normalizeCustomerBoolean(source.profilingConsent ?? source.consensoProfilazione, false),
    profilingConsentDate: cleanCustomerText(source.profilingConsentDate || source.dataConsensoProfilazione),
    dataProcessingNotes: cleanCustomerText(source.dataProcessingNotes || source.noteTrattamentoDati),
    isVip: normalizeCustomerBoolean(source.isVip ?? source.vip, false),
    isBlocked: normalizeCustomerBoolean(source.isBlocked ?? source.bloccato, false),
    blockedReason: cleanCustomerText(source.blockedReason || source.motivoBlocco),
    internalAlert: cleanCustomerText(source.internalAlert || source.alertInterno),
    touristTaxExemptionDefault: normalizeCustomerBoolean(source.touristTaxExemptionDefault ?? source.esenzioneTassaSoggiornoDefault, false),
    touristTaxExemptionReason: cleanCustomerText(source.touristTaxExemptionReason || source.motivoEsenzioneTassaSoggiorno),
    touristTaxExemptionDocument: cleanCustomerText(source.touristTaxExemptionDocument || source.documentoEsenzioneTassaSoggiorno),
    customerStatus: cleanCustomerText(source.customerStatus || source.statoCliente) || "ACTIVE",
    mergedIntoCustomerId: cleanCustomerText(source.mergedIntoCustomerId || source.clienteUnitoInId),
    externalReferences: normalizeExternalReferences(source.externalReferences || source.riferimentiEsterni),
    contacts: normalizeCustomerContacts(source),
    documents: normalizeCustomerDocuments(source),
    notes: note,
    note,
  };
}

function normalizeCustomerPayload(source = {}) {
  const customerType = cleanCustomerText(source.customerType || source.tipoAnagrafica);
  const codiceSdi = cleanCustomerText(customerDraftValue(source, "codiceSdi", "sdiCode"));
  const documents = customerRegistryDocumentsForSave(source);
  const primaryDocument = documents.find((document) => document.documentIsPrimary === true) || documents[0] || {};
  const dataNascita = cleanCustomerText(customerDraftValue(source, "dataNascita", "birthDate"));
  const luogoNascita = cleanCustomerText(customerDraftValue(source, "luogoNascita", "birthPlace"));
  const comuneNascita = cleanCustomerText(customerDraftValue(source, "comuneNascita", "birthCity"));
  const provinciaNascita = cleanCustomerText(customerDraftValue(source, "provinciaNascita", "birthProvince"));
  const statoNascita = cleanCustomerText(customerDraftValue(source, "statoNascita", "birthCountry", "Italia")) || "Italia";
  const cittadinanza = cleanCustomerText(customerDraftValue(source, "cittadinanza", "nationality", "Italia")) || "Italia";
  const sesso = normalizeCustomerGender(customerDraftValue(source, "sesso", "gender"));
  const linguaPreferita = cleanCustomerText(customerDraftValue(source, "linguaPreferita", "preferredLanguage"));
  const pianoPreferito = cleanCustomerText(customerDraftValue(source, "pianoPreferito", "preferredFloor"));
  const allergie = cleanCustomerText(customerDraftValue(source, "allergie", "allergies"));
  const intolleranze = cleanCustomerText(customerDraftValue(source, "intolleranze", "intolerances"));
  const esigenzeSpeciali = cleanCustomerText(customerDraftValue(source, "esigenzeSpeciali", "specialNeeds"));
  const preferenzaArrivo = cleanCustomerText(customerDraftValue(source, "preferenzaArrivo", "arrivalPreference"));
  return {
    country: cleanCustomerText(customerDraftValue(source, "nazione", "country", "Italia")) || "Italia",
    nazione: cleanCustomerText(customerDraftValue(source, "nazione", "country", "Italia")) || "Italia",
    customerType,
    fiscalCode: cleanCustomerText(customerDraftValue(source, "codiceFiscale", "fiscalCode")),
    codiceFiscale: cleanCustomerText(customerDraftValue(source, "codiceFiscale", "fiscalCode")),
    vatNumber: cleanCustomerText(customerDraftValue(source, "partitaIva", "vatNumber")),
    partitaIva: cleanCustomerText(customerDraftValue(source, "partitaIva", "vatNumber")),
    sdiCode: customerType.toLowerCase() === "privato" && !codiceSdi ? "0000000" : codiceSdi,
    codiceSdi: customerType.toLowerCase() === "privato" && !codiceSdi ? "0000000" : codiceSdi,
    surname: cleanCustomerText(customerDraftValue(source, "cognome", "surname")),
    cognome: cleanCustomerText(customerDraftValue(source, "cognome", "surname")),
    name: cleanCustomerText(customerDraftValue(source, "nome", "name")),
    nome: cleanCustomerText(customerDraftValue(source, "nome", "name")),
    businessName: cleanCustomerText(customerDraftValue(source, "ragioneSociale", "businessName")),
    ragioneSociale: cleanCustomerText(customerDraftValue(source, "ragioneSociale", "businessName")),
    birthDate: dataNascita,
    dataNascita,
    birthPlace: luogoNascita,
    luogoNascita,
    birthCity: comuneNascita,
    comuneNascita,
    birthProvince: provinciaNascita,
    provinciaNascita,
    birthCountry: statoNascita,
    statoNascita,
    nationality: cittadinanza,
    cittadinanza,
    gender: sesso,
    sesso,
    address: cleanCustomerText(customerDraftValue(source, "indirizzo", "address")),
    indirizzo: cleanCustomerText(customerDraftValue(source, "indirizzo", "address")),
    streetNumber: cleanCustomerText(customerDraftValue(source, "civico", "streetNumber")),
    civico: cleanCustomerText(customerDraftValue(source, "civico", "streetNumber")),
    city: cleanCustomerText(customerDraftValue(source, "citta", "city")),
    citta: cleanCustomerText(customerDraftValue(source, "citta", "city")),
    province: cleanCustomerText(customerDraftValue(source, "provincia", "province")),
    provincia: cleanCustomerText(customerDraftValue(source, "provincia", "province")),
    postalCode: cleanCustomerText(customerDraftValue(source, "cap", "postalCode")),
    cap: cleanCustomerText(customerDraftValue(source, "cap", "postalCode")),
    contacts: normalizeCustomerContacts(source),
    documents,
    documentoTipo: cleanCustomerText(primaryDocument.documentType),
    documentoNumero: cleanCustomerText(primaryDocument.documentNumber),
    luogoRilascioDocumento: cleanCustomerText(primaryDocument.documentIssuePlace),
    paeseRilascioDocumento: cleanCustomerText(primaryDocument.documentIssueCountry),
    dataRilascioDocumento: cleanCustomerText(primaryDocument.documentIssueDate),
    dataScadenzaDocumento: cleanCustomerText(primaryDocument.documentExpiryDate),
    enteRilascioDocumento: cleanCustomerText(primaryDocument.documentIssuer),
    documentoPrincipale: primaryDocument.documentIsPrimary === true,
    preferredLanguage: linguaPreferita,
    linguaPreferita,
    preferredRoomTypeId: cleanCustomerText(source.preferredRoomTypeId),
    preferredFloor: pianoPreferito,
    pianoPreferito,
    preferredBedTypeId: cleanCustomerText(source.preferredBedTypeId),
    allergies: allergie,
    allergie,
    intolerances: intolleranze,
    intolleranze,
    specialNeeds: esigenzeSpeciali,
    esigenzeSpeciali,
    arrivalPreference: preferenzaArrivo,
    preferenzaArrivo,
    customerTags: normalizeCustomerTags(source.customerTags),
    privacyConsent: normalizeCustomerBoolean(source.privacyConsent, false),
    privacyConsentDate: cleanCustomerText(source.privacyConsentDate),
    marketingConsent: normalizeCustomerBoolean(source.marketingConsent, false),
    marketingConsentDate: cleanCustomerText(source.marketingConsentDate),
    profilingConsent: normalizeCustomerBoolean(source.profilingConsent, false),
    profilingConsentDate: cleanCustomerText(source.profilingConsentDate),
    dataProcessingNotes: cleanCustomerText(source.dataProcessingNotes),
    isVip: normalizeCustomerBoolean(source.isVip, false),
    isBlocked: cleanCustomerText(source.customerStatus).toUpperCase() === "BLOCKED",
    blockedReason: cleanCustomerText(source.blockedReason),
    internalAlert: cleanCustomerText(source.internalAlert),
    touristTaxExemptionDefault: normalizeCustomerBoolean(source.touristTaxExemptionDefault, false),
    touristTaxExemptionReason: cleanCustomerText(source.touristTaxExemptionReason),
    touristTaxExemptionDocument: cleanCustomerText(source.touristTaxExemptionDocument),
    customerStatus: cleanCustomerText(source.customerStatus) || "ACTIVE",
    mergedIntoCustomerId: cleanCustomerText(source.mergedIntoCustomerId),
    externalReferences: normalizeExternalReferences(source.externalReferences),
    notes: cleanCustomerText(customerDraftValue(source, "note", "notes")),
    note: cleanCustomerText(customerDraftValue(source, "note", "notes")),
  };
}

const RELATED_GUEST_TYPES = ["SPOUSE", "PARTNER", "CHILD", "PARENT", "SIBLING", "RELATIVE", "FRIEND", "COLLEAGUE", "COMPANION", "GROUP_MEMBER", "ANIMAL", "OTHER"];

function normalizeRelatedGuestType(value) {
  const token = cleanCustomerText(value).toUpperCase();
  if (!token) return "";
  const legacyMap = { FAMILY_MEMBER: "RELATIVE", FAMILY_HEAD: "RELATIVE", GROUP_HEAD: "GROUP_MEMBER", SINGLE: "OTHER" };
  const normalized = legacyMap[token] || token;
  return RELATED_GUEST_TYPES.includes(normalized) ? normalized : "OTHER";
}

function normalizeCustomerRelatedGuests(source = {}) {
  const rows = Array.isArray(source?.relatedGuests) ? source.relatedGuests : (Array.isArray(source?.ospitiCorrelati) ? source.ospitiCorrelati : []);
  const seen = new Set();
  return rows.map((guest) => ({
    customerId: cleanCustomerText(guest?.customerId || guest?.customer_id || guest?.relatedCustomerId),
    relationshipType: normalizeRelatedGuestType(guest?.relationshipType || guest?.guestRole || guest?.tipoOspite),
  })).filter((guest) => {
    if (!guest.customerId || seen.has(guest.customerId)) return false;
    seen.add(guest.customerId);
    return true;
  });
}

function applyRelatedGuestLinksToCustomerPool(customers = [], ownerCustomerId = "", relatedGuests = []) {
  const ownerId = cleanCustomerText(ownerCustomerId);
  if (!ownerId) return customers;

  const normalized = Array.isArray(relatedGuests) ? relatedGuests : [];
  const nextIds = new Set(normalized.map((guest) => cleanCustomerText(guest?.customerId || guest?.customer_id)).filter(Boolean));
  const ownerCustomer = (customers || []).find((customer) => cleanCustomerText(customer?._id || customer?.id) === ownerId);
  const previousIds = new Set(normalizeCustomerRelatedGuests(ownerCustomer || {}).map((relation) => cleanCustomerText(relation.customerId)).filter(Boolean));

  return (customers || []).map((customer) => {
    const customerId = cleanCustomerText(customer?._id || customer?.id);
    if (!customerId) return customer;

    if (customerId === ownerId) {
      return { ...customer, relatedGuests: normalized, ospitiCorrelati: normalized };
    }

    const existing = normalizeCustomerRelatedGuests(customer);

    if (nextIds.has(customerId)) {
      if (existing.some((relation) => cleanCustomerText(relation.customerId) === ownerId)) return customer;
      const reciprocal = [...existing, { customerId: ownerId, relationshipType: "" }];
      return { ...customer, relatedGuests: reciprocal, ospitiCorrelati: reciprocal };
    }

    if (previousIds.has(customerId)) {
      const reciprocal = existing.filter((relation) => cleanCustomerText(relation.customerId) !== ownerId);
      return { ...customer, relatedGuests: reciprocal, ospitiCorrelati: reciprocal };
    }

    return customer;
  });
}

function buildRelatedGuestRows(customer, bookingGuests = [], customers = [], animalCount = 0, customerTypeCatalog = []) {
  const ownerId = cleanCustomerText(customer?._id || customer?.id);
  const selectedRows = Array.isArray(bookingGuests) ? bookingGuests : [];
  const selectedMap = new Map();
  selectedRows.forEach((guest) => {
    const customerId = cleanCustomerText(guest?.customer_id || guest?.customerId);
    if (!customerId || customerId === ownerId) return;
    selectedMap.set(customerId, guest);
  });

  const result = [];
  const seen = new Set();
  normalizeCustomerRelatedGuests(customer || {}).forEach((relation) => {
    if (!relation.customerId || relation.customerId === ownerId || seen.has(relation.customerId)) return;
    const bookingGuest = selectedMap.get(relation.customerId) || null;
    const linkedCustomer = findCustomerById(customers, relation.customerId);
    if (linkedCustomer && getCustomerRegistryTypeFromCustomer(linkedCustomer, customerTypeCatalog)?.code !== "PRIVATO") return;
    result.push({
      customer_id: relation.customerId,
      customerName: customerDisplayName(linkedCustomer || {}),
      relationshipType: normalizeRelatedGuestType(bookingGuest?.relationshipType || bookingGuest?.guestRole || relation.relationshipType),
      included: Boolean(bookingGuest),
      isMinor: bookingGuest?.isMinor === true,
      guardianCustomerId: cleanCustomerText(bookingGuest?.guardianCustomerId || bookingGuest?.guardian_customer_id),
      guardianFullName: cleanCustomerText(bookingGuest?.guardianFullName),
      guardianRelation: cleanCustomerText(bookingGuest?.guardianRelation),
      guardianConsent: bookingGuest?.guardianConsent === true,
      notes: cleanCustomerText(bookingGuest?.notes),
    });
    seen.add(relation.customerId);
  });

  selectedMap.forEach((bookingGuest, customerId) => {
    if (!customerId || seen.has(customerId) || customerId === ownerId) return;
    const linkedCustomer = findCustomerById(customers, customerId);
    if (linkedCustomer && getCustomerRegistryTypeFromCustomer(linkedCustomer, customerTypeCatalog)?.code !== "PRIVATO") return;
    result.push({
      customer_id: customerId,
      customerName: customerDisplayName(linkedCustomer || {}) || cleanCustomerText(bookingGuest?.customerName),
      relationshipType: normalizeRelatedGuestType(bookingGuest?.relationshipType || bookingGuest?.guestRole),
      included: true,
      isMinor: bookingGuest?.isMinor === true,
      guardianCustomerId: cleanCustomerText(bookingGuest?.guardianCustomerId || bookingGuest?.guardian_customer_id),
      guardianFullName: cleanCustomerText(bookingGuest?.guardianFullName),
      guardianRelation: cleanCustomerText(bookingGuest?.guardianRelation),
      guardianConsent: bookingGuest?.guardianConsent === true,
      notes: cleanCustomerText(bookingGuest?.notes),
    });
    seen.add(customerId);
  });

  const animals = Math.max(0, Math.floor(Number(animalCount || 0)));
  for (let index = 0; index < animals; index += 1) {
    result.push({
      customer_id: "",
      customerName: "",
      relationshipType: "ANIMAL",
      included: true,
      isMinor: false,
      guardianCustomerId: "",
      guardianFullName: "",
      guardianRelation: "",
      guardianConsent: false,
      notes: "",
    });
  }

  return result;
}

function normalizeRelatedGuestsPayload(source = []) {
  const rows = Array.isArray(source) ? source : [];
  const seen = new Set();
  return rows.map((guest) => ({
    customerId: cleanCustomerText(guest?.customer_id || guest?.customerId),
    relationshipType: normalizeRelatedGuestType(guest?.relationshipType || guest?.guestRole),
  })).filter((guest) => {
    if (guest.relationshipType === "ANIMAL" || !guest.customerId || seen.has(guest.customerId)) return false;
    seen.add(guest.customerId);
    return true;
  });
}

function normalizeBookingGuestsPayload(source = []) {
  const rows = Array.isArray(source) ? source : [];
  return rows.filter((guest) => guest?.included === true && normalizeRelatedGuestType(guest?.relationshipType || guest?.guestRole) !== "ANIMAL").map((guest) => {
    const customerId = cleanCustomerText(guest?.customer_id || guest?.customerId);
    return {
      customer_id: customerId,
      guestRole: normalizeRelatedGuestType(guest?.relationshipType || guest?.guestRole),
      isMinor: guest?.isMinor === true,
      guardianCustomerId: cleanCustomerText(guest?.guardianCustomerId || guest?.guardian_customer_id),
      guardianFullName: cleanCustomerText(guest?.guardianFullName),
      guardianRelation: cleanCustomerText(guest?.guardianRelation),
      guardianConsent: guest?.guardianConsent === true,
      notes: cleanCustomerText(guest?.notes),
    };
  }).filter((guest) => guest.customer_id);
}

function getRoomTypeIconVariant(label, value = "") {
  if (value === "ALL") return "all";
  const token = String(label || "").trim().toLowerCase();
  if (/suite/.test(token)) return "suite";
  if (/deluxe|superior|premium|executive|luxury|lusso|comfort/.test(token)) return "premium";
  if (/appart|apartment|studio|residence/.test(token)) return "apartment";
  if (/quad|family|famil/.test(token)) return "family";
  if (/trip/.test(token)) return "triple";
  if (/dopp|double|matrimonial|twin/.test(token)) return "double";
  if (/singol|single/.test(token)) return "single";
  return "room";
}

function getRoomTypeQualityRank(label) {
  const token = String(label || "").trim().toLowerCase();
  if (/presidential|presidenziale|royal/.test(token) && /suite/.test(token)) return 120;
  if (/suite/.test(token) && /junior/.test(token)) return 105;
  if (/suite/.test(token)) return 110;
  if (/executive|luxury|lusso|premium/.test(token)) return 95;
  if (/deluxe/.test(token)) return 90;
  if (/superior/.test(token)) return 85;
  if (/comfort/.test(token)) return 80;
  if (/appart|apartment|residence/.test(token)) return 70;
  if (/studio/.test(token)) return 65;
  if (/quad|family|famil/.test(token)) return 60;
  if (/trip/.test(token)) return 50;
  if (/dopp|double|matrimonial|twin/.test(token)) return 40;
  if (/singol|single/.test(token)) return 30;
  if (/standard|classic|classica|economy|basic/.test(token)) return 20;
  return 25;
}

function RoomBedGlyph({ x = 4, y = 8.2, width = 16, pillows = 2 }) {
  const headboardBottom = y + 10.2;
  const mattressTop = y + 5.15;
  const mattressBottom = y + 9.15;
  const legBottom = y + 11.55;
  const innerLeft = x + 1.7;
  const innerWidth = width - 3.4;

  const pillowGap = 0.8;
  const pillowWidth = pillows === 1 ? Math.min(innerWidth * 0.48, 5.3) : (innerWidth - pillowGap) / 2;
  const pillowY = y + 2.35;
  const pillowHeight = 2.15;
  const firstPillowX = pillows === 1 ? x + (width - pillowWidth) / 2 : innerLeft;
  const secondPillowX = innerLeft + pillowWidth + pillowGap;

  return (
    <g className="booking_room_type_bed_glyph">
      <path d={`M${x} ${headboardBottom}V${y + 1.7}c0-.8.65-1.45 1.45-1.45h${width - 2.9}c.8 0 1.45.65 1.45 1.45v${headboardBottom - y - 1.7}`} />
      <rect className="booking_room_type_icon_fill" x={x} y={mattressTop} width={width} height={mattressBottom - mattressTop} rx="1.05" />
      <path d={`M${x - 0.7} ${mattressBottom}h${width + 1.4}`} />
      <path d={`M${x + 1.4} ${mattressBottom}v${legBottom - mattressBottom}M${x + width - 1.4} ${mattressBottom}v${legBottom - mattressBottom}`} />
      <rect x={firstPillowX} y={pillowY} width={pillowWidth} height={pillowHeight} rx="0.85" />
      {pillows > 1 && <rect x={secondPillowX} y={pillowY} width={pillowWidth} height={pillowHeight} rx="0.85" />}
      <path d={`M${x + 0.9} ${mattressTop + 1.35}h${width - 1.8}`} />
    </g>
  );
}

function RoomBunkGlyph({ x = 15.2, y = 6.6, width = 6.1 }) {
  const right = x + width;
  return (
    <g className="booking_room_type_bunk_glyph">
      <path d={`M${x} ${y}v12.8M${right} ${y}v12.8`} />
      <rect className="booking_room_type_icon_fill" x={x} y={y + 1.5} width={width} height="3.25" rx="0.85" />
      <rect className="booking_room_type_icon_fill" x={x} y={y + 7.25} width={width} height="3.25" rx="0.85" />
      <rect x={x + 0.85} y={y + 2.15} width={width - 3.2} height="1.25" rx="0.55" />
      <rect x={x + 0.85} y={y + 7.9} width={width - 3.2} height="1.25" rx="0.55" />
      <path d={`M${right - 1.15} ${y + 4.9}v2.2M${x} ${y + 5.95}h${width}`} />
    </g>
  );
}

function RoomTypeIcon({ label, value }) {
  const variant = getRoomTypeIconVariant(label, value);

  if (variant === "all") {
    return (
      <span className="booking_room_type_icon is_all" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={4.5} y={7.7} width={15} pillows={2} />
        </svg>
      </span>
    );
  }

  if (variant === "single") {
    return (
      <span className="booking_room_type_icon is_single" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={6.1} y={7.5} width={11.8} pillows={1} />
        </svg>
      </span>
    );
  }

  if (variant === "double") {
    return (
      <span className="booking_room_type_icon is_double" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={4} y={7.5} width={16} pillows={2} />
        </svg>
      </span>
    );
  }

  if (variant === "triple") {
    return (
      <span className="booking_room_type_icon is_triple" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={2.2} y={8.15} width={11.7} pillows={2} />
          <RoomBedGlyph x={15.6} y={10.3} width={6.1} pillows={1} />
        </svg>
      </span>
    );
  }

  if (variant === "family") {
    return (
      <span className="booking_room_type_icon is_family" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={2.2} y={8.25} width={11.3} pillows={2} />
          <RoomBunkGlyph x={15.5} y={5.6} width={6.1} />
        </svg>
      </span>
    );
  }

  if (variant === "apartment") {
    return (
      <span className="booking_room_type_icon is_apartment" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M3.7 20V5.2c0-.85.7-1.55 1.55-1.55h13.5c.85 0 1.55.7 1.55 1.55V20" />
          <path d="M15.6 6.8V20M15.6 10.1h4.7" />
          <RoomBedGlyph x={5.4} y={8.65} width={8.4} pillows={2} />
          <path d="M17.5 14.1h1.35M17.5 16.3h1.35" />
        </svg>
      </span>
    );
  }

  if (variant === "premium") {
    return (
      <span className="booking_room_type_icon is_premium" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={4} y={7.7} width={16} pillows={2} />
          <path className="booking_room_type_icon_accent" d="m18.5 4.1.55 1.1 1.1.55-1.1.55-.55 1.1-.55-1.1-1.1-.55 1.1-.55.55-1.1Z" />
        </svg>
      </span>
    );
  }

  if (variant === "suite") {
    return (
      <span className="booking_room_type_icon is_suite" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <RoomBedGlyph x={3.4} y={7.8} width={17.2} pillows={2} />
          <path className="booking_room_type_icon_accent booking_room_type_suite_crown" d="M8.8 5.25 8.2 3.5l2.35 1.15L12 3.05l1.45 1.6 2.35-1.15-.6 1.75Z" />
          <path d="M8.9 5.25h6.2" />
          <path d="M2.45 15.3h1.3M20.25 15.3h1.3M2.9 15.3v3.9M21.1 15.3v3.9" />
        </svg>
      </span>
    );
  }

  return (
    <span className="booking_room_type_icon is_room" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <RoomBedGlyph x={4.5} y={7.7} width={15} pillows={2} />
      </svg>
    </span>
  );
}

export default function Bookings() {
  const location = useLocation();
  const [periodStart, setPeriodStart] = useState(() => startOfDay(new Date()));
  const [planningMode, setPlanningMode] = useState("HALF");
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomCatalogs, setRoomCatalogs] = useState({ roomTypes: [], bedTypes: [], roomServices: [], countryPrefixes: [], countries: [], municipalities: [], postalCodes: [], languages: [], floorPreferences: [], arrivalPreferences: [], contactTypes: [], documentTypes: [], externalReferenceSources: [] });
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(() => String(readStoredUser()?.defaultSiteId || ""));
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [creditCardCircuits, setCreditCardCircuits] = useState([]);
  const [customerTypeCatalog, setCustomerTypeCatalog] = useState([]);
  const [paymentMethodCatalog, setPaymentMethodCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planningReady, setPlanningReady] = useState(false);
  const [planningFullscreen, setPlanningFullscreen] = useState(false);
  const [error, setError] = useState("");
  const [roomTypeFilter, setRoomTypeFilter] = useState("ALL");
  const [roomTypeFilterOpen, setRoomTypeFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [draggingId, setDraggingId] = useState("");
  const [savingMoveId, setSavingMoveId] = useState("");
  const [draggingMaintenanceId, setDraggingMaintenanceId] = useState("");
  const [savingMaintenanceMoveId, setSavingMaintenanceMoveId] = useState("");
  const [draggingCleaningId, setDraggingCleaningId] = useState("");
  const [savingCleaningMoveId, setSavingCleaningMoveId] = useState("");
  const [hoverCell, setHoverCell] = useState(null);
  const [dropPreview, setDropPreview] = useState(null);
  const [menu, setMenu] = useState(null);
  const [overlayMode, setOverlayMode] = useState("");
  const [activeBookingId, setActiveBookingId] = useState("");
  const [activeRoomId, setActiveRoomId] = useState("");
  const [draft, setDraft] = useState({ ...EMPTY_BOOKING });
  const [overlayError, setOverlayError] = useState("");
  const [customerOverlay, setCustomerOverlay] = useState(null);
  const [customerDraft, setCustomerDraft] = useState({ ...EMPTY_CUSTOMER });
  const [customerValidationErrors, setCustomerValidationErrors] = useState({});
  const [customerValidationActive, setCustomerValidationActive] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [roomOverlay, setRoomOverlay] = useState(null);
  const [roomDraft, setRoomDraft] = useState({ ...EMPTY_ROOM_DRAFT });
  const [roomError, setRoomError] = useState("");
  const [fastCheckoutBooking, setFastCheckoutBooking] = useState(null);
  const [fastStayTab, setFastStayTab] = useState("checkout");
  const [stayTabErrors, setStayTabErrors] = useState({ booking: false, services: false, checkout: false });
  const stayTabErrorBookingIdRef = useRef("");
  const [fastBookingDraft, setFastBookingDraft] = useState(null);
  const [fastServiceCatalog, setFastServiceCatalog] = useState([]);
  const fastCheckoutRequestRef = useRef("");
  const [fastServiceDraft, setFastServiceDraft] = useState({ stay: null, rows: [], removedIds: [] });
  const [fastServiceError, setFastServiceError] = useState("");
  const [fastCheckoutDraft, setFastCheckoutDraft] = useState({ ...EMPTY_CHECKOUT_DRAFT });
  const [fastCheckoutError, setFastCheckoutError] = useState("");
  const [fastCheckoutSaving, setFastCheckoutSaving] = useState(false);
  const planningScrollRef = useRef(null);
  const resetHourlyScrollRef = useRef(false);
  const menuRef = useRef(null);
  const roomTypeFilterRef = useRef(null);
  const roomTypeFilterTypeBufferRef = useRef("");
  const roomTypeFilterTypeBufferTimeRef = useRef(0);
  const statusFilterRef = useRef(null);
  const statusFilterTypeBufferRef = useRef("");
  const statusFilterTypeBufferTimeRef = useRef(0);
  const temporaryPlanningErrorTimerRef = useRef(null);
  const externalEditBookingRef = useRef("");
  const planningRefreshInFlightRef = useRef(false);
  const planningInteractionBusyRef = useRef(false);
  const bookingPermissionLevel = getUserPageLevel(readStoredUser(), "booking");

  const updateStayTabError = useCallback((tab, invalid) => {
    if (!["booking", "services", "checkout"].includes(tab)) return;
    setStayTabErrors((current) => current[tab] === Boolean(invalid) ? current : { ...current, [tab]: Boolean(invalid) });
  }, []);

  function ensureStayTabErrorBooking(booking) {
    const bookingId = String(booking?._id || booking?.id || "");
    if (stayTabErrorBookingIdRef.current === bookingId) return;
    stayTabErrorBookingIdRef.current = bookingId;
    setStayTabErrors({ booking: false, services: false, checkout: false });
  }

  useEffect(() => {
    const handleStayTabValidation = (event) => updateStayTabError(event?.detail?.tab, event?.detail?.invalid);
    document.addEventListener("vesta:stay-tab-validation", handleStayTabValidation);
    return () => document.removeEventListener("vesta:stay-tab-validation", handleStayTabValidation);
  }, [updateStayTabError]);
  const canEditBooking = bookingPermissionLevel >= 2;
  const canDeleteBooking = bookingPermissionLevel >= 3;

  const days = useMemo(() => {
    const from = startOfDay(periodStart);
    const length = planningMode === "HOUR" ? 3 : 92;
    return Array.from({ length }, (_, index) => addDays(from, index));
  }, [periodStart, planningMode]);

  const rangeEnd = useMemo(() => addDays(days[days.length - 1], 1), [days]);

  function showTemporaryPlanningError(message) {
    if (temporaryPlanningErrorTimerRef.current) {
      window.clearTimeout(temporaryPlanningErrorTimerRef.current);
    }

    setError(message);
    temporaryPlanningErrorTimerRef.current = window.setTimeout(() => {
      setError((current) => (current === message ? "" : current));
      temporaryPlanningErrorTimerRef.current = null;
    }, 2000);
  }

  useEffect(() => () => {
    if (temporaryPlanningErrorTimerRef.current) {
      window.clearTimeout(temporaryPlanningErrorTimerRef.current);
    }
  }, []);


  useEffect(() => {
    planningInteractionBusyRef.current = Boolean(draggingId || savingMoveId || draggingMaintenanceId || savingMaintenanceMoveId || draggingCleaningId || savingCleaningMoveId || overlayMode || customerOverlay || roomOverlay || fastCheckoutBooking);
  }, [draggingId, savingMoveId, draggingMaintenanceId, savingMaintenanceMoveId, draggingCleaningId, savingCleaningMoveId, overlayMode, customerOverlay, roomOverlay, fastCheckoutBooking]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const focusId = params.get("focusId");
    const focusDate = params.get("focusDate");
    if (!focusId || !focusDate) return;

    const parsed = new Date(`${focusDate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return;

    const wantedDate = startOfDay(parsed);
    const currentStart = startOfDay(periodStart);
    const currentEnd = startOfDay(rangeEnd);

    if (wantedDate < currentStart || wantedDate >= currentEnd) {
      setPeriodStart(wantedDate);
    }
  }, [location.search, periodStart, rangeEnd]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const editId = String(params.get("editId") || "").trim();
    if (!editId) { externalEditBookingRef.current = ""; return; }
    if (externalEditBookingRef.current === editId || overlayMode || !canEditBooking) return;
    const booking = items.find((item) => String(getBookingId(item)) === editId);
    if (!booking) return;
    externalEditBookingRef.current = editId;
    openOverlay("edit", booking);
  }, [location.search, items, overlayMode, canEditBooking]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    const planningParams = { from: toIsoDate(days[0]), to: toIsoDate(rangeEnd), planning: true, siteId: sites.some((site) => site._id === selectedSiteId) ? selectedSiteId : "" };
    const planningRequest = getBookings(planningParams);
    const roomsRequest = getRooms(planningParams);
    const sitesRequest = planningReady ? Promise.resolve(null) : getSites();
    const circuitsRequest = planningReady ? Promise.resolve(null) : getCreditCardCircuits();

    Promise.all([planningRequest, roomsRequest, sitesRequest, circuitsRequest])
      .then(([bookingsData, roomsData, sitesData, circuitsData]) => {
        if (!alive) return;
        setItems(Array.isArray(bookingsData?.items) ? bookingsData.items : []);
        setRooms(Array.isArray(roomsData?.items) ? roomsData.items : []);
        const loadedRoomCatalogs = roomsData?.catalogs || {};
        setRoomCatalogs((current) => ({
          ...current,
          roomTypes: Array.isArray(loadedRoomCatalogs.roomTypes) ? loadedRoomCatalogs.roomTypes : current.roomTypes,
          bedTypes: Array.isArray(loadedRoomCatalogs.bedTypes) ? loadedRoomCatalogs.bedTypes : current.bedTypes,
          roomServices: Array.isArray(loadedRoomCatalogs.roomServices) ? loadedRoomCatalogs.roomServices : current.roomServices,
        }));

        if (!planningReady) {
          const availableSites = Array.isArray(sitesData?.items) ? sitesData.items.filter((site) => site.active !== false) : [];
          setSites(availableSites);
          if (!availableSites.some((site) => site._id === selectedSiteId)) {
            const preferred = String(readStoredUser()?.defaultSiteId || "");
            setSelectedSiteId(availableSites.some((site) => site._id === preferred) ? preferred : (availableSites[0]?._id || ""));
          }
          setCreditCardCircuits(Array.isArray(circuitsData?.items) ? circuitsData.items : []);
          setPlanningReady(true);
        }
      })
      .catch(() => {
        if (!alive) return;
        showTemporaryPlanningError("Impossibile caricare il planning");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [days, rangeEnd, selectedSiteId]);

  const refreshPlanningSilently = useCallback(async (manual = false) => {
    if (!planningReady || planningInteractionBusyRef.current || (!manual && document.hidden)) return;
    if (planningRefreshInFlightRef.current) { if (manual) setManualRefreshing(true); return; }
    planningRefreshInFlightRef.current = true;
    if (manual) setManualRefreshing(true);
    try {
      const planningParams = { from: toIsoDate(days[0]), to: toIsoDate(rangeEnd), planning: true, siteId: sites.some((site) => site._id === selectedSiteId) ? selectedSiteId : "" };
      const [bookingsData, roomsData] = await Promise.all([
        getBookings(planningParams),
        getRooms(planningParams),
      ]);
      setItems(Array.isArray(bookingsData?.items) ? bookingsData.items : []);
      setRooms(Array.isArray(roomsData?.items) ? roomsData.items : []);
    } catch (_error) {
      // Il refresh automatico non interrompe l'uso; quello manuale segnala gli errori.
      if (manual) showTemporaryPlanningError("Impossibile aggiornare il planning");
    } finally {
      planningRefreshInFlightRef.current = false;
      setManualRefreshing(false);
    }
  }, [planningReady, days, rangeEnd, selectedSiteId, sites]);

  useEffect(() => {
    if (!planningReady) return undefined;

    const timer = window.setInterval(() => { void refreshPlanningSilently(); }, 30000);
    const handleVisibility = () => {
      if (!document.hidden) void refreshPlanningSilently();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [planningReady, refreshPlanningSilently]);

  useEffect(() => {
    if (!planningReady) return undefined;

    let alive = true;
    Promise.all([
      getCustomers().catch(() => ({ items: [] })),
      getCatalogItems("customer-types", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("payment-methods", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("countries", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("municipalities", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("postal-codes", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("languages", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("floor-preferences", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("arrival-preferences", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("contact-types", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("document-types", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("external-reference-sources", { includeInactive: true }).catch(() => ({ items: [] })),
    ]).then(([customersData, customerTypesData, paymentMethodsData, countriesData, municipalitiesData, postalCodesData, languagesData, floorPreferencesData, arrivalPreferencesData, contactTypesData, documentTypesData, externalReferenceSourcesData]) => {
      if (!alive) return;
      setCustomers(Array.isArray(customersData?.items) ? customersData.items : []);
      setCustomerTypeCatalog(Array.isArray(customerTypesData?.items) ? customerTypesData.items : []);
      setPaymentMethodCatalog(Array.isArray(paymentMethodsData?.items) ? paymentMethodsData.items : []);
      setRoomCatalogs((current) => ({
        ...current,
        countryPrefixes: Array.isArray(countriesData?.items) ? countriesData.items : [],
        countries: Array.isArray(countriesData?.items) ? countriesData.items : [],
        municipalities: Array.isArray(municipalitiesData?.items) ? municipalitiesData.items : [],
        postalCodes: Array.isArray(postalCodesData?.items) ? postalCodesData.items : [],
        languages: Array.isArray(languagesData?.items) ? languagesData.items : [],
        floorPreferences: Array.isArray(floorPreferencesData?.items) ? floorPreferencesData.items : [],
        arrivalPreferences: Array.isArray(arrivalPreferencesData?.items) ? arrivalPreferencesData.items : [],
        contactTypes: Array.isArray(contactTypesData?.items) ? contactTypesData.items : [],
        documentTypes: Array.isArray(documentTypesData?.items) ? documentTypesData.items : [],
        externalReferenceSources: Array.isArray(externalReferenceSourcesData?.items) ? externalReferenceSourcesData.items : [],
      }));
    });

    return () => { alive = false; };
  }, [planningReady]);

  const roomTypeFilterOptions = useMemo(() => {
    const counts = new Map();
    (rooms || []).forEach((room) => {
      const type = String(room.room_type || "Camera").trim() || "Camera";
      counts.set(type, (counts.get(type) || 0) + 1);
    });
    return [
      { value: "ALL", label: "Tutte le Tipologie", count: rooms.length },
      ...Array.from(counts.entries())
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => {
          const rankDifference = getRoomTypeQualityRank(a.label) - getRoomTypeQualityRank(b.label);
          if (rankDifference !== 0) return rankDifference;
          return a.label.localeCompare(b.label, "it", { numeric: true });
        }),
    ];
  }, [rooms]);

  const activeRoomTypeOption = roomTypeFilterOptions.find((option) => option.value === roomTypeFilter) || roomTypeFilterOptions[0];
  const filteredRooms = useMemo(() => {
    if (roomTypeFilter === "ALL") return rooms;
    return rooms.filter((room) => (String(room.room_type || "Camera").trim() || "Camera") === roomTypeFilter);
  }, [roomTypeFilter, rooms]);

  const roomTypeFilteredItems = useMemo(() => {
    if (roomTypeFilter === "ALL") return items;
    const roomNumbers = new Set(filteredRooms.map((room) => String(room.room_number || "").trim()).filter(Boolean));
    return items.filter((item) => roomNumbers.has(String(item.room_number || "").trim()));
  }, [filteredRooms, items, roomTypeFilter]);

  const planningStatusCounts = useMemo(() => roomTypeFilteredItems.reduce((acc, item) => {
    const status = normalizeStatus(item.status);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {}), [roomTypeFilteredItems]);

  const activeStatusOption = STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) || STATUS_FILTER_OPTIONS[0];
  const allStatusVisibleItems = useMemo(() => roomTypeFilteredItems.filter((item) => !isCancelledStatus(item.status)), [roomTypeFilteredItems]);

  const filteredItems = useMemo(() => {
    if (statusFilter === "ALL") return allStatusVisibleItems;
    return roomTypeFilteredItems.filter((item) => normalizeStatus(item.status) === statusFilter);
  }, [allStatusVisibleItems, roomTypeFilteredItems, statusFilter]);

  const groups = useMemo(() => getRoomGroups(filteredItems, filteredRooms), [filteredItems, filteredRooms]);
  // Indici derivati: l'ordine originale viene conservato e, per camere omonime,
  // viene mantenuta la prima corrispondenza (come Array.find).
  const roomByNumber = useMemo(() => {
    const index = new Map();
    rooms.forEach((room) => {
      const key = String(room.room_number || "");
      if (!index.has(key)) index.set(key, room);
    });
    return index;
  }, [rooms]);
  const bookingsByRoom = useMemo(() => {
    const index = new Map();
    filteredItems.forEach((booking) => {
      const key = String(booking.room_number || "");
      if (!index.has(key)) index.set(key, []);
      index.get(key).push(booking);
    });
    return index;
  }, [filteredItems]);
  const slotsPerDay = planningMode === "HOUR" ? 24 : 2;
  const planningSlotWidth = planningMode === "HOUR" ? 42 : 28;
  const slotDurationMs = planningMode === "HOUR" ? 3600000 : 43200000;
  const totalPlanningSlots = days.length * slotsPerDay;
  const timelineStart = startOfDay(days[0]).getTime();

  const stats = useMemo(() => {
    const statsStart = startOfDay(periodStart);
    const statsEnd = planningMode === "HOUR" ? addDays(statsStart, 3) : addMonths(statsStart, 1);
    const periodItems = filteredItems.filter((item) => {
      const checkIn = planningDateValue(item.check_in);
      const checkOut = planningDateValue(item.check_out);
      return checkIn && checkOut && checkIn < statsEnd && checkOut > statsStart;
    });
    const visible = periodItems.length;
    const occupied = periodItems.reduce((sum, item) => {
      const checkIn = planningDateValue(item.check_in);
      const checkOut = planningDateValue(item.check_out);
      if (!checkIn || !checkOut) return sum;
      const effectiveIn = checkIn < statsStart ? statsStart : checkIn;
      const effectiveOut = checkOut > statsEnd ? statsEnd : checkOut;
      const duration = Math.max(0, effectiveOut.getTime() - effectiveIn.getTime());
      return sum + (planningMode === "HOUR" ? Math.round((duration / 3600000) * 10) / 10 : Math.max(1, Math.ceil(duration / 86400000)));
    }, 0);
    const revenue = periodItems.reduce((sum, item) => sum + Number(item.total_amount || 0), 0);
    const pending = periodItems.filter((item) => normalizeStatus(item.status) === "PENDING").length;
    return { visible, occupied, revenue, pending };
  }, [filteredItems, periodStart, planningMode]);

  function getTimelinePosition(startValue, endValue) {
    const startDate = planningDateValue(startValue);
    const endDate = planningDateValue(endValue);
    if (!startDate || !endDate) return { start: 0, span: 1 };
    const rawStart = (startDate.getTime() - timelineStart) / slotDurationMs;
    const rawEnd = (endDate.getTime() - timelineStart) / slotDurationMs;
    const startSlot = Math.max(0, Math.min(totalPlanningSlots - 0.1, rawStart));
    const endSlot = Math.max(startSlot + 0.1, Math.min(totalPlanningSlots, rawEnd));
    return { start: startSlot, span: Math.max(0.1, endSlot - startSlot) };
  }

  function getBookingPosition(booking) {
    return getTimelinePosition(booking.check_in, booking.check_out);
  }

  function candidateRangeForSlot(startSlot, span, sourceStartValue = "") {
    const sourceStart = planningDateValue(sourceStartValue);
    const sourceDayStart = sourceStart ? startOfDay(sourceStart).getTime() : 0;
    const offsetInsideSlot = sourceStart
      ? ((sourceStart.getTime() - sourceDayStart) % slotDurationMs + slotDurationMs) % slotDurationMs
      : 0;
    const nextStart = new Date(timelineStart + (startSlot * slotDurationMs) + offsetInsideSlot);
    return { nextStart, nextEnd: new Date(nextStart.getTime() + (span * slotDurationMs)) };
  }

  function isBookingOverlapping(bookingId, room, startSlot, span, sourceStartValue = "") {
    const { nextStart, nextEnd } = candidateRangeForSlot(startSlot, span, sourceStartValue);
    return items.some((item) => {
      if (getBookingId(item) === bookingId) return false;
      if (String(item.room_number || "") !== String(room)) return false;
      if (normalizeStatus(item.status) === "CANCELLED") return false;
      const itemStart = planningDateValue(item.check_in);
      const itemEnd = planningDateValue(item.check_out);
      return itemStart && itemEnd && nextStart < itemEnd && nextEnd > itemStart;
    });
  }

  function bookingsForRoom(room) {
    return bookingsByRoom.get(String(room)) || [];
  }

  function hydrateRoomPlanningState(roomItem) {
    const now = new Date();
    const activeMaintenance = (roomItem.maintenances || []).find((item) => {
      const startDate = planningDateValue(item.date_start);
      const endDate = planningDateValue(item.date_end);
      return item.active !== false && startDate && endDate && startDate <= now && endDate > now;
    }) || null;
    const activeCleaning = (roomItem.cleanings || []).find((item) => {
      const startDate = planningDateValue(item.date_start);
      const endDate = planningDateValue(item.date_end);
      return item.active !== false && !["DONE", "CANCELLED"].includes(String(item.status || "").toUpperCase()) && startDate && endDate && startDate <= now && endDate > now;
    }) || null;
    return { ...roomItem, active_maintenance: activeMaintenance, active_cleaning: activeCleaning };
  }

  function maintenancesForRoom(room) {
    const roomItem = roomByNumber.get(String(room));
    const visibleStart = startOfDay(days[0]);
    const visibleEnd = startOfDay(rangeEnd);
    return (roomItem?.maintenances || []).filter((item) => {
      if (item.active === false) return false;
      const startDate = planningDateValue(item.date_start);
      const endDate = planningDateValue(item.date_end);
      return startDate && endDate && startDate < visibleEnd && endDate > visibleStart;
    });
  }

  function cleaningsForRoom(room) {
    if (planningMode !== "HOUR") return [];
    const roomItem = roomByNumber.get(String(room));
    const visibleStart = startOfDay(days[0]);
    const visibleEnd = startOfDay(rangeEnd);
    return (roomItem?.cleanings || []).filter((item) => {
      if (item.active === false || String(item.status || "").toUpperCase() === "CANCELLED") return false;
      const startDate = planningDateValue(item.date_start);
      const endDate = planningDateValue(item.date_end);
      return startDate && endDate && startDate < visibleEnd && endDate > visibleStart;
    });
  }

  function getMaintenancePosition(maintenance) {
    return getTimelinePosition(maintenance.date_start, maintenance.date_end);
  }

  function getCleaningPosition(cleaning) {
    return getTimelinePosition(cleaning.date_start, cleaning.date_end);
  }

  function isMaintenanceOverlapping(room, startSlot, span, excludedMaintenanceId = "", sourceStartValue = "") {
    const { nextStart, nextEnd } = candidateRangeForSlot(startSlot, span, sourceStartValue);
    const roomItem = roomByNumber.get(String(room));
    return (roomItem?.maintenances || []).some((item) => {
      if (item.active === false) return false;
      if (excludedMaintenanceId && String(item._id || "") === String(excludedMaintenanceId)) return false;
      const itemStart = planningDateValue(item.date_start);
      const itemEnd = planningDateValue(item.date_end);
      return itemStart && itemEnd && nextStart < itemEnd && nextEnd > itemStart;
    });
  }

  function isCleaningOverlapping(room, startSlot, span, excludedCleaningId = "", blockingOnly = true, sourceStartValue = "") {
    const { nextStart, nextEnd } = candidateRangeForSlot(startSlot, span, sourceStartValue);
    const roomItem = roomByNumber.get(String(room));
    return (roomItem?.cleanings || []).some((item) => {
      if (item.active === false) return false;
      if (excludedCleaningId && String(item._id || "") === String(excludedCleaningId)) return false;
      if (blockingOnly && item.blocks_room === false) return false;
      if (["DONE", "CANCELLED"].includes(String(item.status || "").toUpperCase())) return false;
      const itemStart = planningDateValue(item.date_start);
      const itemEnd = planningDateValue(item.date_end);
      return itemStart && itemEnd && nextStart < itemEnd && nextEnd > itemStart;
    });
  }

  function selectedBooking() {
    return items.find((item) => getBookingId(item) === activeBookingId) || null;
  }

  function handleTodayClick() {
    const today = startOfDay(new Date());
    setPeriodStart(today);
    requestAnimationFrame(() => {
      if (planningScrollRef.current) planningScrollRef.current.scrollLeft = 0;
    });
  }

  function clearPlanningDragState() {
    setDraggingId("");
    setDraggingMaintenanceId("");
    setDraggingCleaningId("");
    setDropPreview(null);
  }

  function findMaintenanceDragRecord(maintenanceId) {
    const normalizedId = String(maintenanceId || "");
    if (!normalizedId) return null;
    for (const roomItem of rooms) {
      const maintenance = (roomItem.maintenances || []).find((item) => String(item._id || "") === normalizedId);
      if (maintenance) return { roomItem, maintenance };
    }
    return null;
  }

  function findCleaningDragRecord(cleaningId) {
    const normalizedId = String(cleaningId || "");
    if (!normalizedId) return null;
    for (const roomItem of rooms) {
      const cleaning = (roomItem.cleanings || []).find((item) => String(item._id || "") === normalizedId);
      if (cleaning) return { roomItem, cleaning };
    }
    return null;
  }

  function handleDragStart(event, booking) {
    if (event.button !== 0) return;
    if (!canEditBooking) {
      event.preventDefault();
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    const id = getBookingId(booking);
    setDraggingMaintenanceId("");
    setDraggingCleaningId("");
    setDraggingId(id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", `booking:${id}`);
  }

  function handleMaintenanceDragStart(event, maintenance) {
    if (event.button !== 0) return;
    if (!canEditBooking) {
      event.preventDefault();
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    const maintenanceId = String(maintenance?._id || "");
    if (!maintenanceId || savingMaintenanceMoveId) {
      event.preventDefault();
      return;
    }
    setDraggingId("");
    setDraggingCleaningId("");
    setDraggingMaintenanceId(maintenanceId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", `maintenance:${maintenanceId}`);
  }

  function handleCleaningDragStart(event, cleaning) {
    if (event.button !== 0) return;
    if (!canEditBooking) {
      event.preventDefault();
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    const cleaningId = String(cleaning?._id || "");
    if (!cleaningId || savingCleaningMoveId) {
      event.preventDefault();
      return;
    }
    setDraggingId("");
    setDraggingMaintenanceId("");
    setDraggingCleaningId(cleaningId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", `cleaning:${cleaningId}`);
  }

  function handleDragEnd() {
    clearPlanningDragState();
  }

  function allowDrop(event) {
    if (!canEditBooking) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  function recordSpan(startValue, endValue) {
    const startDate = planningDateValue(startValue);
    const endDate = planningDateValue(endValue);
    if (!startDate || !endDate) return 1;
    return Math.max(0.1, (endDate.getTime() - startDate.getTime()) / slotDurationMs);
  }

  function getDraggingSpan() {
    if (draggingMaintenanceId) {
      const record = findMaintenanceDragRecord(draggingMaintenanceId);
      return record ? recordSpan(record.maintenance.date_start, record.maintenance.date_end) : 1;
    }
    if (draggingCleaningId) {
      const record = findCleaningDragRecord(draggingCleaningId);
      return record ? recordSpan(record.cleaning.date_start, record.cleaning.date_end) : 1;
    }
    const booking = items.find((item) => getBookingId(item) === draggingId);
    return booking ? recordSpan(booking.check_in, booking.check_out) : 1;
  }

  function updateDropPreview(room, roomType, slotIndex) {
    if (!draggingId && !draggingMaintenanceId && !draggingCleaningId) return;
    const safeSlot = Math.max(0, Math.min(totalPlanningSlots - 1, Number(slotIndex || 0)));
    const dayIndex = Math.floor(safeSlot / slotsPerDay);
    const hour = planningMode === "HOUR" ? safeSlot % 24 : null;
    const half = planningMode === "HOUR" ? "HOUR" : (safeSlot % 2 === 0 ? "AM" : "PM");
    const span = getDraggingSpan();
    const isMaintenanceDrag = Boolean(draggingMaintenanceId);
    const isCleaningDrag = Boolean(draggingCleaningId);
    const cleaningRecord = isCleaningDrag ? findCleaningDragRecord(draggingCleaningId)?.cleaning : null;
    const sourceStartValue = isMaintenanceDrag
      ? findMaintenanceDragRecord(draggingMaintenanceId)?.maintenance?.date_start
      : isCleaningDrag
        ? cleaningRecord?.date_start
        : items.find((item) => getBookingId(item) === draggingId)?.check_in;
    const invalidBooking = isCleaningDrag
      ? false
      : isBookingOverlapping(isMaintenanceDrag ? "" : draggingId, room, safeSlot, span, sourceStartValue);
    const invalidMaintenance = isMaintenanceOverlapping(room, safeSlot, span, isMaintenanceDrag ? draggingMaintenanceId : "", sourceStartValue);
    const invalidCleaning = draggingId
      ? false
      : isCleaningOverlapping(room, safeSlot, span, isCleaningDrag ? draggingCleaningId : "", true, sourceStartValue);
    const invalid = invalidBooking || invalidMaintenance || invalidCleaning;
    setHoverCell({ room: String(room), dayIndex, slotIndex: safeSlot, half, hour });
    setDropPreview({ room: String(room), roomType: String(roomType), slotIndex: safeSlot, dayIndex, half, hour, span, invalid });
  }

  function handleCellDragOver(event, room, roomType, slotIndex) {
    allowDrop(event);
    updateDropPreview(room, roomType, slotIndex);
  }

  function handleRowDragOver(event, room, roomType) {
    allowDrop(event);
    updateDropPreview(room, roomType, getDropSlotIndexFromRow(event));
  }

  function buildMovedRange(startValue, endValue, safeSlot) {
    const originalStart = planningDateValue(startValue);
    const originalEnd = planningDateValue(endValue);
    if (!originalStart || !originalEnd) return null;
    const durationMs = Math.max(60000, originalEnd.getTime() - originalStart.getTime());
    const originalDayStart = startOfDay(originalStart).getTime();
    const offsetInsideSlot = ((originalStart.getTime() - originalDayStart) % slotDurationMs + slotDurationMs) % slotDurationMs;
    const nextStart = new Date(timelineStart + (safeSlot * slotDurationMs) + offsetInsideSlot);
    const nextEnd = new Date(nextStart.getTime() + durationMs);
    return { nextStart, nextEnd };
  }

  async function moveMaintenanceFromDrop(maintenanceId, room, safeSlot) {
    const sourceRecord = findMaintenanceDragRecord(maintenanceId);
    const targetRoom = roomByNumber.get(String(room));
    if (!sourceRecord?.roomItem?._id || !targetRoom?._id) throw new Error("Camera manutenzione non trovata");
    const movedRange = buildMovedRange(sourceRecord.maintenance.date_start, sourceRecord.maintenance.date_end, safeSlot);
    if (!movedRange) throw new Error("Periodo manutenzione non valido");
    const payload = { date_start: valueToDateTimeInput(movedRange.nextStart), date_end: valueToDateTimeInput(movedRange.nextEnd), description: String(sourceRecord.maintenance.description || "").trim() };
    const sourceRoomId = String(sourceRecord.roomItem._id);
    const targetRoomId = String(targetRoom._id);
    if (sourceRoomId === targetRoomId) {
      const saved = (await updateRoomMaintenance(sourceRoomId, maintenanceId, payload))?.item;
      if (!saved) throw new Error("Spostamento manutenzione non salvato");
      setRooms((current) => current.map((item) => String(item._id) !== sourceRoomId ? item : hydrateRoomPlanningState({ ...item, maintenances: (item.maintenances || []).map((row) => String(row._id) === String(saved._id) ? saved : row) })));
      return;
    }
    let created = null;
    try {
      created = (await createRoomMaintenance(targetRoomId, payload))?.item || null;
      if (!created) throw new Error("Spostamento manutenzione non salvato");
      await deleteRoomMaintenance(sourceRoomId, maintenanceId);
    } catch (err) {
      if (created?._id) { try { await deleteRoomMaintenance(targetRoomId, created._id); } catch {} }
      throw err;
    }
    setRooms((current) => current.map((item) => {
      const id = String(item._id || "");
      if (id === sourceRoomId) return hydrateRoomPlanningState({ ...item, maintenances: (item.maintenances || []).map((row) => String(row._id) === String(maintenanceId) ? { ...row, active: false } : row) });
      if (id === targetRoomId) return hydrateRoomPlanningState({ ...item, maintenances: [...(item.maintenances || []), created] });
      return item;
    }));
  }

  async function moveCleaningFromDrop(cleaningId, room, safeSlot) {
    const sourceRecord = findCleaningDragRecord(cleaningId);
    const targetRoom = roomByNumber.get(String(room));
    if (!sourceRecord?.roomItem?._id || !targetRoom?._id) throw new Error("Camera pulizia non trovata");
    const movedRange = buildMovedRange(sourceRecord.cleaning.date_start, sourceRecord.cleaning.date_end, safeSlot);
    if (!movedRange) throw new Error("Periodo pulizia non valido");
    const payload = {
      date_start: valueToDateTimeInput(movedRange.nextStart), date_end: valueToDateTimeInput(movedRange.nextEnd),
      cleaning_type: sourceRecord.cleaning.cleaning_type || "TURNOVER", status: sourceRecord.cleaning.status || "PLANNED",
      priority: sourceRecord.cleaning.priority || "NORMAL", assigned_to: sourceRecord.cleaning.assigned_to || "",
      blocks_room: sourceRecord.cleaning.blocks_room !== false, note: sourceRecord.cleaning.note || "",
    };
    const sourceRoomId = String(sourceRecord.roomItem._id);
    const targetRoomId = String(targetRoom._id);
    if (sourceRoomId === targetRoomId) {
      const saved = (await updateRoomCleaning(sourceRoomId, cleaningId, payload))?.item;
      if (!saved) throw new Error("Spostamento pulizia non salvato");
      setRooms((current) => current.map((item) => String(item._id) !== sourceRoomId ? item : hydrateRoomPlanningState({ ...item, cleanings: (item.cleanings || []).map((row) => String(row._id) === String(saved._id) ? saved : row) })));
      return;
    }
    let created = null;
    try {
      created = (await createRoomCleaning(targetRoomId, payload))?.item || null;
      if (!created) throw new Error("Spostamento pulizia non salvato");
      await deleteRoomCleaning(sourceRoomId, cleaningId);
    } catch (err) {
      if (created?._id) { try { await deleteRoomCleaning(targetRoomId, created._id); } catch {} }
      throw err;
    }
    setRooms((current) => current.map((item) => {
      const id = String(item._id || "");
      if (id === sourceRoomId) return hydrateRoomPlanningState({ ...item, cleanings: (item.cleanings || []).map((row) => String(row._id) === String(cleaningId) ? { ...row, active: false, status: "CANCELLED" } : row) });
      if (id === targetRoomId) return hydrateRoomPlanningState({ ...item, cleanings: [...(item.cleanings || []), created] });
      return item;
    }));
  }

  async function handleDrop(event, room, roomType, slotIndex) {
    event.preventDefault();
    event.stopPropagation();
    if (!canEditBooking) {
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      clearPlanningDragState();
      return;
    }
    const transferValue = event.dataTransfer.getData("text/plain");
    const safeSlot = dropPreview?.room === String(room) ? dropPreview.slotIndex : Math.max(0, Math.min(totalPlanningSlots - 1, Number(slotIndex || 0)));
    const transferredMaintenanceId = transferValue.startsWith("maintenance:") ? transferValue.slice("maintenance:".length) : "";
    const transferredCleaningId = transferValue.startsWith("cleaning:") ? transferValue.slice("cleaning:".length) : "";
    const maintenanceId = draggingMaintenanceId || transferredMaintenanceId;
    const cleaningId = draggingCleaningId || transferredCleaningId;
    setError("");
    setDropPreview(null);

    if (maintenanceId) {
      if (savingMaintenanceMoveId) return;
      const sourceRecord = findMaintenanceDragRecord(maintenanceId);
      if (!sourceRecord) return clearPlanningDragState();
      const span = recordSpan(sourceRecord.maintenance.date_start, sourceRecord.maintenance.date_end);
      if (isBookingOverlapping("", room, safeSlot, span, sourceRecord.maintenance.date_start) || isMaintenanceOverlapping(room, safeSlot, span, maintenanceId, sourceRecord.maintenance.date_start) || isCleaningOverlapping(room, safeSlot, span, "", true, sourceRecord.maintenance.date_start)) {
        showTemporaryPlanningError("Spostamento bloccato: periodo occupato"); clearPlanningDragState(); return;
      }
      setSavingMaintenanceMoveId(maintenanceId);
      try { await moveMaintenanceFromDrop(maintenanceId, room, safeSlot); }
      catch (err) { showTemporaryPlanningError(err?.response?.data?.error || err?.message || "Spostamento manutenzione non salvato"); }
      finally { setSavingMaintenanceMoveId(""); clearPlanningDragState(); setHoverCell(null); }
      return;
    }

    if (cleaningId) {
      if (savingCleaningMoveId) return;
      const sourceRecord = findCleaningDragRecord(cleaningId);
      if (!sourceRecord) return clearPlanningDragState();
      const span = recordSpan(sourceRecord.cleaning.date_start, sourceRecord.cleaning.date_end);
      if (isMaintenanceOverlapping(room, safeSlot, span, "", sourceRecord.cleaning.date_start) || isCleaningOverlapping(room, safeSlot, span, cleaningId, true, sourceRecord.cleaning.date_start)) {
        showTemporaryPlanningError("Spostamento bloccato: periodo occupato"); clearPlanningDragState(); return;
      }
      setSavingCleaningMoveId(cleaningId);
      try { await moveCleaningFromDrop(cleaningId, room, safeSlot); }
      catch (err) { showTemporaryPlanningError(err?.response?.data?.error || err?.message || "Spostamento pulizia non salvato"); }
      finally { setSavingCleaningMoveId(""); clearPlanningDragState(); setHoverCell(null); }
      return;
    }

    const bookingId = transferValue.startsWith("booking:") ? transferValue.slice("booking:".length) : transferValue;
    const booking = items.find((item) => getBookingId(item) === bookingId);
    if (!booking || savingMoveId) return;
    const movedRange = buildMovedRange(booking.check_in, booking.check_out, safeSlot);
    if (!movedRange) return;
    const span = recordSpan(booking.check_in, booking.check_out);
    if (isBookingOverlapping(bookingId, room, safeSlot, span, booking.check_in)) { showTemporaryPlanningError("Spostamento bloccato: prenotazione sovrapposta"); clearPlanningDragState(); return; }
    if (isMaintenanceOverlapping(room, safeSlot, span, "", booking.check_in)) { showTemporaryPlanningError("Spostamento bloccato: camera in manutenzione"); clearPlanningDragState(); return; }
    const previousItems = items;
    const payload = { room_number: String(room), room_type: String(roomType), check_in: valueToDateTimeInput(movedRange.nextStart), check_out: valueToDateTimeInput(movedRange.nextEnd) };
    setSavingMoveId(bookingId);
    setItems((current) => current.map((item) => getBookingId(item) === bookingId ? { ...item, ...payload } : item));
    try {
      const updated = (await updateBookingPlanning(bookingId, { ...payload, site_id: selectedSiteId }))?.item;
      if (updated) setItems((current) => current.map((item) => getBookingId(item) === bookingId ? updated : item));
    } catch (err) {
      setItems(previousItems);
      const moveError = err?.response?.data?.error || "Spostamento prenotazione non salvato";
      if (/prenotazione sovrapposta nella stessa camera/i.test(String(moveError))) showTemporaryPlanningError(moveError);
      else showTemporaryPlanningError(moveError);
    } finally {
      setSavingMoveId(""); clearPlanningDragState();
    }
  }

  function getDropSlotIndexFromRow(event) {
    const cells = Array.from(event.currentTarget.querySelectorAll(":scope > .booking_grid_slot"));
    const exactIndex = cells.findIndex((cell) => {
      const rect = cell.getBoundingClientRect();
      return event.clientX >= rect.left && event.clientX < rect.right;
    });
    if (exactIndex >= 0) return exactIndex;
    const rect = event.currentTarget.getBoundingClientRect();
    const relativeX = Math.max(0, event.clientX - rect.left);
    return Math.max(0, Math.min(totalPlanningSlots - 1, Math.floor(relativeX / planningSlotWidth)));
  }

  function handleRowDrop(event, room, roomType) {
    handleDrop(event, room, roomType, getDropSlotIndexFromRow(event));
  }

  function showUnauthorizedPlanningLabel(x, y) {
    setMenu({ x, y, type: "unauthorized" });
  }

  function handleRoomContextMenu(event, room, roomType) {
    event.preventDefault();
    event.stopPropagation();
    setActiveBookingId("");
    if (!canEditBooking) {
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    setMenu({ x: event.clientX, y: event.clientY, type: "room", room, roomType });
  }

  function handleEmptyDayContextMenu(event, room, roomType, day, half = "AM", hour = null) {
    event.preventDefault();
    event.stopPropagation();
    setActiveBookingId("");
    setMenu({ x: event.clientX, y: event.clientY, type: "empty", room, roomType, day, half, hour });
  }

  function handleMaintenanceContextMenu(event, room, roomType, maintenance) {
    event.preventDefault();
    event.stopPropagation();
    setActiveBookingId("");
    if (!canEditBooking && planningMode !== "HALF") {
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    setMenu({ x: event.clientX, y: event.clientY, type: "maintenance", room, roomType, maintenance, day: getClickedPlanningDay(event) });
  }

  function handleCleaningContextMenu(event, room, roomType, cleaning) {
    event.preventDefault();
    event.stopPropagation();
    setActiveBookingId("");
    if (!canEditBooking) {
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    setMenu({ x: event.clientX, y: event.clientY, type: "cleaning", room, roomType, cleaning });
  }

  // Nella vista oraria il giorno selezionato deve essere il primo, anche se il planning era già scorso orizzontalmente.
  useLayoutEffect(() => {
    if (!resetHourlyScrollRef.current || planningMode !== "HOUR") return;
    resetHourlyScrollRef.current = false;
    if (planningScrollRef.current) planningScrollRef.current.scrollLeft = 0;
  }, [planningMode, periodStart]);

  function switchToClickedDay() {
    if (!menu?.day) return;
    resetHourlyScrollRef.current = true;
    setPeriodStart(startOfDay(new Date(menu.day)));
    setPlanningMode("HOUR");
    setMenu(null);
  }

  function switchToMonthDay() {
    setPeriodStart((current) => new Date(current.getFullYear(), current.getMonth(), 1));
    setPlanningMode("HALF");
    setMenu(null);
    requestAnimationFrame(() => { if (planningScrollRef.current) planningScrollRef.current.scrollLeft = 0; });
  }

  // Sulle barre occupate si usa la posizione del mouse nel reticolo, non la data di check-in.
  function getClickedPlanningDay(event) {
    if (planningMode !== "HALF") return null;
    const row = event.currentTarget?.closest?.(".booking_timeline_row");
    const slots = row?.querySelectorAll(":scope > .booking_grid_slot");
    if (!slots?.length) return null;
    const firstSlotRect = slots[0].getBoundingClientRect();
    if (!firstSlotRect.width) return null;
    const slot = Math.max(0, Math.min(slots.length - 1, Math.floor((event.clientX - firstSlotRect.left) / firstSlotRect.width)));
    return days[Math.floor(slot / 2)] || null;
  }

  function getPlanningMenuContext() {
    if (!menu || !["empty", "room"].includes(menu.type)) return null;
    return { room: menu.room, roomType: menu.roomType, day: menu.day, half: menu.half, hour: menu.hour };
  }

  function getPlanningOperationForContext(rows = [], planning = {}, cleaning = false) {
    if (!planning?.day) return null;
    const targetStart = startOfDay(new Date(planning.day));
    let durationMs = 43200000;
    if (planning.hour !== null && planning.hour !== undefined && Number.isInteger(Number(planning.hour))) {
      targetStart.setHours(Math.max(0, Math.min(23, Number(planning.hour))), 0, 0, 0);
      durationMs = 3600000;
    } else if (planning.half === "PM") {
      targetStart.setHours(12, 0, 0, 0);
    }
    const targetEnd = new Date(targetStart.getTime() + durationMs);
    return (rows || []).find((item) => {
      if (item.active === false) return false;
      if (cleaning && ["DONE", "CANCELLED"].includes(String(item.status || "").toUpperCase())) return false;
      const startDate = planningDateValue(item.date_start);
      const endDate = planningDateValue(item.date_end);
      return startDate && endDate && startDate < targetEnd && endDate > targetStart;
    }) || null;
  }

  function getPlanningAvailabilityContext() {
    const planning = getPlanningMenuContext();
    if (!planning) return null;
    const roomItem = rooms.find((item) => String(item.room_number || "") === String(planning.room || ""));
    return {
      ...planning,
      cleaning: ROOM_STATUS_FALLBACK.cleaning,
      active_maintenance: getPlanningOperationForContext(roomItem?.maintenances, planning, false),
      active_cleaning: getPlanningOperationForContext(roomItem?.cleanings, planning, true),
    };
  }

  function getPlanningDraft(overrides = {}, defaultStayDays = 1) {
    const checkInDate = parseFlexibleDate(overrides.check_in) || startOfDay(new Date());
    const stayDays = Math.max(1, Math.min(365, Number.parseInt(defaultStayDays, 10) || 1));
    return {
      ...EMPTY_BOOKING,
      ...overrides,
      check_in: formatDateForInput(checkInDate),
      check_in_time: overrides.check_in_time || "12:00",
      check_out: overrides.check_out ? normalizeDateFieldValue(overrides.check_out) : formatDateForInput(addDays(checkInDate, stayDays)),
      check_out_time: overrides.check_out_time || "11:59",
    };
  }

  function getSiteForRoom(roomNumber = "") {
    const activeSites = (sites || []).filter((item) => item.active !== false);
    const roomItem = (rooms || []).find((item) => String(item.room_number || "") === String(roomNumber || ""));
    const roomSite = roomItem?.site_id ? activeSites.find((item) => String(item._id || "") === String(roomItem.site_id || "")) : null;
    return roomSite || activeSites.find((item) => String(item.siteNature || "").toUpperCase() === "OPERATIVE") || activeSites[0] || null;
  }

  function getActiveSiteDefaults(roomNumber = "") {
    return getSiteForRoom(roomNumber)?.defaults || null;
  }

  function applyDefaultField(target, fieldName, defaultField) {
    if (!defaultField || !Object.prototype.hasOwnProperty.call(defaultField, "value")) return;
    if (typeof defaultField.value === "boolean" || String(defaultField.value ?? "").trim()) target[fieldName] = defaultField.value ?? "";
  }

  function getDefaultStayDays(roomNumber = "") {
    const field = getActiveSiteDefaults(roomNumber)?.booking?.stayDays;
    return Math.max(1, Math.min(365, Number.parseInt(field?.value, 10) || 3));
  }

  function buildDefaultsDraft(mode, roomNumber = "") {
    const defaults = getActiveSiteDefaults(roomNumber);
    const next = {};
    if (mode === "new" || mode === "checkin") {
      applyDefaultField(next, "payment_method", defaults?.booking?.paymentMethod);
      applyDefaultField(next, "invoice_same_as_customer", defaults?.customer?.invoiceSameAsCustomer);
      if (mode === "new") applyDefaultField(next, "status", defaults?.booking?.status);
    }
    return next;
  }

  function openOverlay(mode, booking = null, planning = null) {
    if (mode === "edit" && booking) ensureStayTabErrorBooking(booking);
    setMenu(null);
    setOverlayError("");
    setActiveBookingId(getBookingId(booking));
    setActiveRoomId("");

    if (["roomEdit", "roomMaintenance", "roomCleaning"].includes(mode)) {
      const roomItem = rooms.find((item) => String(item.room_number || "") === String(planning?.room || ""));
      const planningDay = planning?.day ? toIsoDate(planning.day) : "";
      const planningHour = planning?.hour !== null && planning?.hour !== undefined && Number.isInteger(Number(planning.hour)) ? Math.max(0, Math.min(23, Number(planning.hour))) : null;
      const half = planning?.half === "PM" ? "PM" : "AM";
      const defaultStartTime = planningHour !== null ? `${String(planningHour).padStart(2, "0")}:00` : (half === "PM" ? "12:00" : "00:00");
      const defaultEndTime = planningHour !== null ? `${String((planningHour + 1) % 24).padStart(2, "0")}:00` : (half === "PM" ? "23:59" : "11:59");
      const defaultEndDay = planningHour === 23 && planningDay ? toIsoDate(addDays(new Date(`${planningDay}T00:00:00`), 1)) : planningDay;
      const contextMaintenance = mode === "roomMaintenance" ? planning?.maintenance : null;
      const contextCleaning = mode === "roomCleaning" ? planning?.cleaning : null;
      setActiveRoomId(String(roomItem?._id || ""));
      setDraft({
        ...EMPTY_ROOM_DRAFT,
        ...(roomItem || {}),
        room_number: planning?.room || roomItem?.room_number || "",
        room_type: planning?.roomType || roomItem?.room_type || "",
        ...(contextMaintenance ? {
          maintenance_context_edit: true,
          maintenance_edit_id: contextMaintenance._id || "",
          maintenance_date_start: maintenanceDatePart(contextMaintenance.date_start),
          maintenance_time_start: maintenanceTimePart(contextMaintenance.date_start),
          maintenance_date_end: maintenanceDatePart(contextMaintenance.date_end),
          maintenance_time_end: maintenanceTimePart(contextMaintenance.date_end),
          maintenance_description: contextMaintenance.description || "",
        } : mode === "roomMaintenance" && planningDay ? {
          maintenance_edit_id: "", maintenance_date_start: planningDay, maintenance_time_start: defaultStartTime,
          maintenance_date_end: defaultEndDay, maintenance_time_end: defaultEndTime, maintenance_description: "",
        } : {}),
        ...(contextCleaning ? {
          cleaning_context_edit: true,
          cleaning_edit_id: contextCleaning._id || "",
          cleaning_date_start: maintenanceDatePart(contextCleaning.date_start),
          cleaning_time_start: maintenanceTimePart(contextCleaning.date_start),
          cleaning_date_end: maintenanceDatePart(contextCleaning.date_end),
          cleaning_time_end: maintenanceTimePart(contextCleaning.date_end),
          cleaning_type: contextCleaning.cleaning_type || "TURNOVER",
          cleaning_task_status: contextCleaning.status || "PLANNED",
          cleaning_priority: contextCleaning.priority || "NORMAL",
          cleaning_assigned_to: contextCleaning.assigned_to || "",
          cleaning_blocks_room: contextCleaning.blocks_room !== false,
          cleaning_note: contextCleaning.note || "",
        } : mode === "roomCleaning" && planningDay ? {
          cleaning_edit_id: "", cleaning_date_start: planningDay, cleaning_time_start: defaultStartTime,
          cleaning_date_end: defaultEndDay, cleaning_time_end: defaultEndTime,
          cleaning_type: "TURNOVER", cleaning_task_status: "PLANNED", cleaning_priority: "NORMAL",
          cleaning_assigned_to: "", cleaning_blocks_room: true, cleaning_note: "",
        } : {}),
      });
    } else if (mode === "checkin" && booking) {
      const bookingDraft = bookingToDraft(booking);
      const selectedCustomer = findCustomerById(customers, bookingDraft.customer_id);
      setDraft({
        ...bookingDraft,
        relatedGuests: buildRelatedGuestRows(selectedCustomer, bookingDraft.guests, customers, bookingDraft.animals, customerTypeCatalog),
        document_type: bookingDraft.document_type || CHECKIN_DEFAULTS.document_type,
        payment_method: bookingDraft.payment_method || buildDefaultsDraft(mode, bookingDraft.room_number).payment_method || CHECKIN_DEFAULTS.payment_method,
        status: "CHECKED_IN",
      });
    } else if (mode === "new" || mode === "checkin") {
      const baseDraft = getPlanningDraft({
        room_number: planning?.room || EMPTY_BOOKING.room_number,
        room_type: planning?.roomType || EMPTY_BOOKING.room_type,
        check_in: planning?.day ? toIsoDate(planning.day) : toIsoDate(startOfDay(new Date())),
        check_in_time: planning?.hour !== undefined && planning?.hour !== null ? `${String(Number(planning.hour)).padStart(2, "0")}:00` : (planning?.half === "AM" ? "00:00" : "12:00"),
        status: mode === "checkin" ? "CHECKED_IN" : "CONFIRMED",
      }, mode === "new" ? getDefaultStayDays(planning?.room) : 1);
      setDraft({ ...baseDraft, ...CHECKIN_DEFAULTS, ...buildDefaultsDraft(mode, planning?.room) });
    } else if (mode === "availability") {
      setDraft({
        ...getPlanningDraft({
          room_number: planning?.room || EMPTY_BOOKING.room_number,
          room_type: planning?.roomType || EMPTY_BOOKING.room_type,
          check_in: planning?.day ? toIsoDate(planning.day) : toIsoDate(startOfDay(new Date())),
        }),
        cleaning_status: ROOM_STATUS_FALLBACK.cleaning,
        active_maintenance: planning?.active_maintenance || null,
        active_cleaning: planning?.active_cleaning || null,
      });
    } else {
      const bookingDraft = bookingToDraft(booking);
      const selectedCustomer = findCustomerById(customers, bookingDraft.customer_id);
      setDraft({ ...bookingDraft, relatedGuests: buildRelatedGuestRows(selectedCustomer, bookingDraft.guests, customers, bookingDraft.animals, customerTypeCatalog) });
    }

    setOverlayMode(mode === "checkin" ? "edit" : mode);
  }


  function upsertCustomerInState(item) {
    if (!item?._id) return;
    setCustomers((current) => {
      const exists = current.some((row) => String(row._id) === String(item._id));
      return exists ? current.map((row) => String(row._id) === String(item._id) ? item : row) : [...current, item];
    });
  }

  async function openCustomerRegistryRecord({ target, customerId = "", customerName = "" }) {
    setMenu(null);
    setCustomerError("");
    setCustomerValidationErrors({});
    setCustomerValidationActive(false);

    const currentId = cleanCustomerText(customerId);
    const currentName = cleanCustomerText(customerName);
    let selected = findCustomerById(customers, currentId);

    if (!selected && (currentId || currentName)) {
      try {
        const data = await getCustomers({ q: currentName || currentId });
        const candidates = data?.items || [];
        selected = candidates.find((item) => String(item._id || item.id || "") === currentId)
          || candidates.find((item) => customerDisplayName(item) === currentName)
          || null;
      } catch {
        selected = null;
      }
    }

    const defaults = getActiveSiteDefaults(draft.room_number);
    const defaultContactType = cleanCustomerText(defaults?.customer?.contactType?.value) || "Cellulare";
    const defaultDocumentType = cleanCustomerText(defaults?.customer?.documentType?.value) || "Carta identità";
    const defaultPrefixId = cleanCustomerText(defaults?.customer?.countryPrefixId?.value);
    const defaultPreference = (key) => cleanCustomerText(defaults?.customer?.[key]?.value);
    const isInvoiceTarget = target === "invoice";
    const configuredInvoiceType = defaults?.customer?.invoiceCustomerType?.value
      ? getCustomerRegistryType(defaults.customer.invoiceCustomerType.value)
      : null;
    const targetCustomerType = isInvoiceTarget ? configuredInvoiceType : getCustomerRegistryType("PRIVATO");
    const newCustomerDraft = normalizeSharedCustomerDraft({
      customerTypeId: targetCustomerType?.code || "",
      customerType: targetCustomerType?.description || "",
      businessName: targetCustomerType?.businessNameRequired ? currentName : "",
      nationality: "Italia",
      country: "Italia",
      contacts: [{ contactType: defaultContactType, countryPrefixId: defaultPrefixId, contactValue: "", interlocutor: "", ...contactPreferenceDefaults(defaultContactType), primary: false }],
      documents: [{ documentType: defaultDocumentType, documentNumber: "", documentIssueCountry: "", documentIssuePlace: "", documentIssueDate: "", documentExpiryDate: "", documentIssuer: "", documentIsPrimary: false }],
      preferredLanguage: defaultPreference("preferredLanguage"),
      preferredRoomTypeId: defaultPreference("preferredRoomTypeId"),
      preferredFloor: defaultPreference("preferredFloor"),
      preferredBedTypeId: defaultPreference("preferredBedTypeId"),
      arrivalPreference: defaultPreference("arrivalPreference"),
    }, customerTypeCatalog);

    setCustomerDraft(selected ? normalizeSharedCustomerDraft(selected) : newCustomerDraft);
    setCustomerOverlay({ target, mode: selected ? "edit" : "new", id: selected?._id || "" });
  }

  function openBookingGuestRegistry(booking) {
    const bookingDraft = bookingToDraft(booking);
    return openCustomerRegistryRecord({
      target: { type: "bookingContextGuest", bookingId: getBookingId(booking) },
      customerId: bookingDraft.customer_id,
      customerName: bookingDraft.guest_name,
    });
  }

  function openCustomerRegistry(target) {
    const isRelatedGuest = typeof target === "object" && target?.type === "guest";
    const guestIndex = Number(target?.index);
    const guestRow = isRelatedGuest && Number.isInteger(guestIndex) ? (draft.relatedGuests || [])[guestIndex] || null : null;
    const isInvoice = target === "invoice";
    return openCustomerRegistryRecord({
      target,
      customerId: isRelatedGuest ? guestRow?.customer_id : (isInvoice ? draft.invoice_customer_id : draft.customer_id),
      customerName: isRelatedGuest ? guestRow?.customerName : (isInvoice ? draft.invoice_name : draft.guest_name),
    });
  }

  function updateCustomerDraft(field, value, relatedFields = {}) {
    setCustomerError("");
    const validationKey = field === "customerTypeId" ? "customerType" : field;
    let candidate = { ...customerDraft };
    if (field === "customerTypeId") {
      const type = customerTypeCatalog.find((item) => String(item._id) === String(value));
      candidate = { ...candidate, customerTypeId: value, customerType: type?.description || "" };
    } else if (field === "customerStatus") {
      candidate = { ...candidate, customerStatus: value, isBlocked: value === "BLOCKED" };
    } else {
      candidate = { ...candidate, [field]: value, ...(relatedFields && typeof relatedFields === "object" ? relatedFields : {}) };
      const canonicalTwin = CUSTOMER_DRAFT_TWIN_FIELDS[field];
      const legacyTwin = CUSTOMER_DRAFT_TWIN_FIELDS_REVERSE[field];
      if (canonicalTwin) candidate[canonicalTwin] = value;
      if (legacyTwin) candidate[legacyTwin] = value;
    }

    const candidateTypeRow = customerTypeCatalog.find((item) => String(item?._id || item?.code || "") === String(candidate.customerTypeId || "") || cleanCustomerText(item?.description).toLocaleLowerCase("it") === cleanCustomerText(candidate.customerType).toLocaleLowerCase("it"));
    const previousTypeRow = customerTypeCatalog.find((item) => String(item?._id || item?.code || "") === String(customerDraft.customerTypeId || "") || cleanCustomerText(item?.description).toLocaleLowerCase("it") === cleanCustomerText(customerDraft.customerType).toLocaleLowerCase("it"));
    const candidateType = getCustomerRegistryType(candidateTypeRow?.code || candidateTypeRow?.description || candidate.customerType || candidate.customerTypeId);
    const previousType = getCustomerRegistryType(previousTypeRow?.code || previousTypeRow?.description || customerDraft.customerType || customerDraft.customerTypeId);
    const candidateCountry = cleanCustomerText(candidate.country || candidate.nazione).toLocaleLowerCase("it");
    const previousCountry = cleanCustomerText(customerDraft.country || customerDraft.nazione).toLocaleLowerCase("it");
    const candidateIsItalian = ["italia", "italy", "it"].includes(candidateCountry);
    const previousIsItalian = ["italia", "italy", "it"].includes(previousCountry);
    const candidateIsPrivateItalian = candidateIsItalian && candidateType?.entityType === "PERSON" && candidateType?.vatNumberRequired === false;
    const candidateIsPrivateForeign = !candidateIsItalian && candidateType?.code === "PRIVATO";
    if (!candidateIsItalian) {
      candidate.fiscalCode = "";
      candidate.codiceFiscale = "";
    }
    if (candidateIsPrivateItalian) {
      candidate.vatNumber = "";
      candidate.partitaIva = "";
    } else if (candidateIsPrivateForeign) {
      candidate.vatNumber = FOREIGN_PRIVATE_TAX_ID;
      candidate.partitaIva = FOREIGN_PRIVATE_TAX_ID;
    } else if (!candidateIsItalian && field === "customerTypeId" && previousType?.code === "PRIVATO" && cleanCustomerText(candidate.vatNumber || candidate.partitaIva) === FOREIGN_PRIVATE_TAX_ID) {
      candidate.vatNumber = "";
      candidate.partitaIva = "";
    }

    if (!candidateIsItalian) {
      candidate.sdiCode = "XXXXXXX";
      candidate.codiceSdi = "XXXXXXX";
      candidate.postalCode = "00000";
      candidate.cap = "00000";
    } else {
      if (!previousIsItalian && field === "country" && cleanCustomerText(candidate.postalCode || candidate.cap) === "00000") {
        candidate.postalCode = "";
        candidate.cap = "";
      }
      if (candidateType?.code === "PRIVATO") {
        candidate.sdiCode = "0000000";
        candidate.codiceSdi = "0000000";
      } else if (!previousIsItalian && field === "country" && cleanCustomerText(candidate.sdiCode || candidate.codiceSdi).toUpperCase() === "XXXXXXX") {
        candidate.sdiCode = "";
        candidate.codiceSdi = "";
      } else if (field === "customerTypeId" && previousType?.code === "PRIVATO" && cleanCustomerText(candidate.sdiCode || candidate.codiceSdi) === "0000000") {
        candidate.sdiCode = "";
        candidate.codiceSdi = "";
      } else if (field === "customerTypeId" && previousType?.sdiLength && candidateType?.sdiLength && previousType.sdiLength !== candidateType.sdiLength) {
        candidate.sdiCode = "";
        candidate.codiceSdi = "";
      }
    }

    setCustomerValidationErrors((current) => {
      const keysToClear = new Set([validationKey, ...Object.keys(relatedFields && typeof relatedFields === "object" ? relatedFields : {})]);
      if (field === "country" || field === "customerTypeId") {
        keysToClear.add("fiscalCode");
        keysToClear.add("vatNumber");
        keysToClear.add("sdiCode");
      }
      if (field === "country") keysToClear.add("postalCode");
      return Object.fromEntries(Object.entries(current).filter(([key]) => ![...keysToClear].some((target) => key === target || key.startsWith(`${target}.`))));
    });
    setCustomerDraft(candidate);
  }

  function clearCustomerValidationError(fieldKey) {
    if (!fieldKey) return;
    setCustomerValidationErrors((current) => {
      if (!current[fieldKey]) return current;
      const next = { ...current };
      delete next[fieldKey];
      return next;
    });
  }

  function updateCustomerPrivacyConsent(flagField, dateField, checked) {
    const consentDate = checked ? formatVestaDate(new Date()) : "";
    setCustomerDraft((current) => ({
      ...current,
      [flagField]: checked,
      ...(checked ? { [dateField]: consentDate } : {}),
    }));
    setCustomerValidationErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== flagField && key !== dateField)));
  }

  async function saveCustomerRegistry() {
    setCustomerValidationActive(true);
    const payload = normalizeCustomerForSave(customerDraft);
    const nextValidationErrors = validateCustomerRegistry(payload, { customerTypeCatalog, municipalities: roomCatalogs.municipalities || [], countries: roomCatalogs.countries || [], requiredPreferences: { preferredLanguage: getActiveSiteDefaults(draft.room_number)?.customer?.preferredLanguage?.required === true, preferredRoomTypeId: getActiveSiteDefaults(draft.room_number)?.customer?.preferredRoomTypeId?.required === true, preferredFloor: getActiveSiteDefaults(draft.room_number)?.customer?.preferredFloor?.required === true, preferredBedTypeId: getActiveSiteDefaults(draft.room_number)?.customer?.preferredBedTypeId?.required === true, arrivalPreference: getActiveSiteDefaults(draft.room_number)?.customer?.arrivalPreference?.required === true } });
    if (Object.keys(nextValidationErrors).length) {
      setCustomerValidationErrors(nextValidationErrors);
      setCustomerError(Object.values(nextValidationErrors)[0] || "Dati Errati e/o Incompleti");
      const firstKey = firstValidationKey(nextValidationErrors);
      window.requestAnimationFrame(() => document.querySelectorAll(".customer_overlay_card [data-field-key]").forEach((field) => {
        if (field.getAttribute("data-field-key") === firstKey) field.scrollIntoView({ behavior: "smooth", block: "center" });
      }));
      return;
    }
    setCustomerValidationErrors({});
    setCustomerError("");
    try {
      const data = customerOverlay?.mode === "edit" && customerOverlay?.id
        ? await updateCustomer(customerOverlay.id, payload)
        : await createCustomer(payload);
      const item = data?.item;
      if (item) {
        upsertCustomerInState(item);
        const target = customerOverlay?.target;
        const isGuestTarget = typeof target === "object" && target?.type === "guest";
        if (isGuestTarget) {
          const guestIndex = Number(target.index);
          setDraft((current) => {
            const rows = Array.isArray(current.relatedGuests) ? [...current.relatedGuests] : [];
            const existing = rows[guestIndex] || {};
            rows[guestIndex] = {
              ...existing,
              customer_id: item._id,
              customerName: customerDisplayName(item),
              isMinor: existing.isMinor === true,
              included: existing.included !== false,
              relationshipType: normalizeRelatedGuestType(existing.relationshipType),
            };
            return { ...current, relatedGuests: rows };
          });
        } else if (target === "invoice") {
          setDraft((current) => ({ ...current, invoice_customer_id: item._id, invoice_name: customerDisplayName(item), invoice_email: customerContactValue(item, ["Email"]), invoice_phone: customerPhoneMobileValue(item) }));
        } else if (typeof target === "object" && target?.type === "bookingContextGuest") {
          // L'anagrafica aperta dal planning modifica solo l'ospite associato alla prenotazione.
        } else {
          setDraft((current) => {
            const customerName = customerDisplayName(item);
            const customerEmail = customerContactValue(item, ["Email"]);
            const customerPhone = customerPhoneMobileValue(item);
            const invoiceSync = current.invoice_same_as_customer !== false
              ? { invoice_customer_id: item._id, invoice_name: customerName, invoice_email: customerEmail, invoice_phone: customerPhone }
              : {};
            return { ...current, customer_id: item._id, guest_name: customerName, guest_email: customerEmail, guest_phone: customerPhone, ...invoiceSync };
          });
        }
      }
      setCustomerOverlay(null);
    } catch (err) {
      const serverErrors = err?.response?.data?.errors || {};
      setCustomerValidationErrors(serverErrors);
      setCustomerError(err?.response?.data?.error || Object.values(serverErrors)[0] || "Impossibile salvare l'anagrafica");
      if (Object.keys(serverErrors).length) {
        const firstKey = firstValidationKey(serverErrors);
        window.requestAnimationFrame(() => document.querySelectorAll(".customer_overlay_card [data-field-key]").forEach((field) => {
          if (field.getAttribute("data-field-key") === firstKey) field.scrollIntoView({ behavior: "smooth", block: "center" });
        }));
      }
    }
  }

  async function selectBookingCustomer(target, customer) {
    if (!customer) return;
    if (target !== "invoice" && getCustomerRegistryTypeFromCustomer(customer, customerTypeCatalog)?.code !== "PRIVATO") return;
    const customerId = String(customer._id || customer.id || "");
    const customerName = customerDisplayName(customer);
    const customerEmail = customerContactValue(customer, ["Email"]);
    const customerPhone = customerPhoneMobileValue(customer);

    setOverlayError("");
    if (target === "invoice") {
      setDraft((current) => ({
        ...current,
        invoice_customer_id: customerId,
        invoice_name: customerName,
        invoice_email: customerEmail,
        invoice_phone: customerPhone,
      }));
      return;
    }

    const customerPool = customers.some((item) => String(item._id) === customerId) ? customers : [...customers, customer];
    const initialRelatedGuests = buildRelatedGuestRows(customer, [], customerPool, 0, customerTypeCatalog);
    setDraft((current) => {
      const invoiceSync = current.invoice_same_as_customer !== false
        ? {
            invoice_customer_id: customerId,
            invoice_name: customerName,
            invoice_email: customerEmail,
            invoice_phone: customerPhone,
          }
        : {};
      return {
        ...current,
        customer_id: customerId,
        guest_name: customerName,
        guest_email: customerEmail,
        guest_phone: customerPhone,
        relatedGuests: initialRelatedGuests,
        ...invoiceSync,
      };
    });

    const shouldUseLastBooking = overlayMode === "new" || (overlayMode === "checkin" && !activeBookingId);
    if (!shouldUseLastBooking) return;
    try {
      const data = await getLastBookingGuests(customerId);
      const sourceGuests = Array.isArray(data?.guests) ? data.guests : [];
      const relatedGuests = buildRelatedGuestRows(customer, sourceGuests, customerPool, Number(data?.animals || data?.item?.animals || 0), customerTypeCatalog);
      setDraft((current) => String(current.customer_id || "") === customerId ? { ...current, relatedGuests } : current);
    } catch {
      // Mantiene comunque l'elenco persistente, semplicemente senza preselezione storica.
    }
  }

  function closeOverlay() {
    setOverlayMode("");
    setActiveBookingId("");
    setActiveRoomId("");
    setOverlayError("");
    stayTabErrorBookingIdRef.current = "";
    setStayTabErrors({ booking: false, services: false, checkout: false });
  }

  function updateDraft(field, value) {
    setOverlayError("");
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === "guest_name" && value !== current.guest_name) {
        next.customer_id = "";
        next.relatedGuests = [];
      }
      if (field === "invoice_name" && value !== current.invoice_name) next.invoice_customer_id = "";
      if (field === "room_number" && !["roomEdit", "roomMaintenance", "roomCleaning"].includes(overlayMode)) {
        const selectedRoom = rooms.find((item) => String(item.room_number || "") === String(value || ""));
        next.room_type = selectedRoom?.room_type || "";
        next.room_type_id = selectedRoom?.room_type_id || "";
      }
      if (field === "cleaning_type") {
        next.cleaning_blocks_room = !["STAYOVER", "INSPECTION"].includes(String(value || "").toUpperCase());
      }
      if (field === "invoice_same_as_customer") {
        if (value !== false) {
          next.invoice_customer_id = next.customer_id || "";
          next.invoice_name = next.guest_name || "";
          next.invoice_email = next.guest_email || "";
          next.invoice_phone = next.guest_phone || "";
        } else {
          next.invoice_customer_id = "";
          next.invoice_name = "";
          next.invoice_email = "";
          next.invoice_phone = "";
        }
      }
      if (["customer_id", "guest_name", "guest_email", "guest_phone"].includes(field) && next.invoice_same_as_customer !== false) {
        next.invoice_customer_id = next.customer_id || "";
        next.invoice_name = next.guest_name || "";
        next.invoice_email = next.guest_email || "";
        next.invoice_phone = next.guest_phone || "";
      }
      return next;
    });
  }

  function openRoomRegistryFromBooking() {
    setRoomError("");
    const selectedRoom = rooms.find((item) => String(item.room_number || "") === String(draft.room_number || ""));
    setRoomDraft({
      ...EMPTY_ROOM_DRAFT,
      ...(selectedRoom || {}),
      room_number: selectedRoom?.room_number || draft.room_number || "",
      room_type_id: selectedRoom?.room_type_id || draft.room_type_id || "",
      room_type: selectedRoom?.room_type || draft.room_type || "",
    });
    setRoomOverlay({ mode: selectedRoom ? "edit" : "new", id: selectedRoom?._id || "" });
  }

  function updateRoomDraft(field, value) {
    setRoomError("");
    setRoomDraft((current) => {
      if (field === "room_type_id") {
        const selectedType = (roomCatalogs.roomTypes || []).find((item) => String(item._id) === String(value));
        return { ...current, room_type_id: value, price: Number(selectedType?.defaultPrice || 0) };
      }
      return { ...current, [field]: value };
    });
  }

  async function saveRoomRegistry() {
    if (!String(roomDraft.room_number || "").trim() || !String(roomDraft.room_type_id || "").trim() || !String(roomDraft.site_id || "").trim()) {
      setRoomError("Completa i Dati Richiesti");
      return;
    }
    try {
      const selectedType = (roomCatalogs.roomTypes || []).find((item) => item._id === roomDraft.room_type_id);
      const payload = {
        ...roomDraft,
        room_number: String(roomDraft.room_number || "").trim(),
        room_type_id: roomDraft.room_type_id,
        room_type: selectedType?.description || roomDraft.room_type || "",
      };
      const data = roomOverlay?.mode === "edit" && roomOverlay?.id ? await updateRoom(roomOverlay.id, payload) : await createRoom(payload);
      const item = data?.item;
      if (item) {
        setRooms((current) => {
          const exists = current.some((row) => String(row._id) === String(item._id));
          return exists ? current.map((row) => String(row._id) === String(item._id) ? item : row) : [...current, item];
        });
        setDraft((current) => ({ ...current, room_number: item.room_number, room_type: item.room_type, room_type_id: item.room_type_id }));
      }
      setRoomOverlay(null);
    } catch (err) {
      setRoomError(err?.response?.data?.error || "Dati Errati");
    }
  }

  function buildPayload() {
    const invoiceSameAsCustomer = draft.invoice_same_as_customer !== false;
    const customerId = String(draft.customer_id || "").trim();
    const selectedCustomer = findCustomerById(customers, customerId);
    const guestName = String(draft.guest_name || "").trim();
    const guestEmail = String(draft.guest_email || "").trim();
    const guestPhone = selectedCustomer ? customerPhoneMobileValue(selectedCustomer) : String(draft.guest_phone || "").trim();
    const payments = normalizeBookingPayments(draft.payments).map((payment) => ({
      paymentMethod: String(payment.paymentMethod || "").trim(),
      paymentType: String(payment.paymentType || (payment.isDeposit === true ? "ADVANCE" : "PAYMENT")).toUpperCase(),
      notes: String(payment.notes || "").trim(),
      amount: Math.max(0, normalizeMoneyToNumber(payment.amount)),
      isDeposit: String(payment.paymentType || "").toUpperCase() === "ADVANCE" || payment.isDeposit === true,
      advanceDocument: payment.advanceDocument ? { ...payment.advanceDocument } : undefined,
    }));
    const depositAmount = payments
      .filter((payment) => ["ADVANCE", "DEPOSIT"].includes(payment.paymentType))
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const selectedRoom = rooms.find((item) => String(item.room_number || "") === String(draft.room_number || ""));
    const occupancyCounts = deriveBookingOccupancyCounts(draft, customers);
    return {
      ...draft,
      notes: String(draft.notes || "").trim(),
      guest_name: guestName,
      guest_email: guestEmail,
      guest_phone: guestPhone,
      customer_id: customerId,
      invoice_same_as_customer: invoiceSameAsCustomer,
      invoice_customer_id: invoiceSameAsCustomer ? customerId : String(draft.invoice_customer_id || "").trim(),
      invoice_name: invoiceSameAsCustomer ? guestName : String(draft.invoice_name || "").trim(),
      invoice_email: invoiceSameAsCustomer ? guestEmail : String(draft.invoice_email || "").trim(),
      invoice_phone: invoiceSameAsCustomer ? guestPhone : String(draft.invoice_phone || "").trim(),
      relatedGuests: normalizeRelatedGuestsPayload(draft.relatedGuests),
      guests: normalizeBookingGuestsPayload(draft.relatedGuests),
      document_type: String(draft.document_type || "").trim(),
      document_number: String(draft.document_number || "").trim(),
      payment_method: String(draft.payment_method || "").trim(),
      credit_card: String(draft.credit_card || "").trim(),
      credit_card_circuit_id: String(draft.credit_card_circuit_id || "").trim(),
      credit_card_number: String(draft.credit_card_number || "").replace(/\D/g, ""),
      credit_card_expiry: normalizeMonthFieldValue(draft.credit_card_expiry || ""),
      room_number: String(draft.room_number || "").trim(),
      room_type: String(selectedRoom?.room_type || draft.room_type || "Camera").trim() || "Camera",
      adults: occupancyCounts.adults,
      children: occupancyCounts.children,
      animals: occupancyCounts.animals,
      payments,
      total_amount: normalizeMoneyToNumber(draft.total_amount),
      deposit_amount: depositAmount,
      status: normalizeStatus(draft.status),
      check_in: draft.check_in ? toCheckInDateTimeFromInput(draft.check_in, draft.check_in_time) : "",
      check_out: draft.check_out ? toCheckOutDateTimeFromInput(draft.check_out, draft.check_out_time) : "",
    };
  }

  function validateDraft(payload) {
    if (overlayMode === "roomEdit") {
      if (!payload.room_number || !payload.room_type_id || !String(draft.site_id || "").trim()) return "Completa i Dati Richiesti";
      return "";
    }
    if (overlayMode === "roomMaintenance") {
      const maintenanceStart = maintenanceDateTimeValue(draft.maintenance_date_start, draft.maintenance_time_start);
      const maintenanceEnd = maintenanceDateTimeValue(draft.maintenance_date_end, draft.maintenance_time_end);
      if (!maintenanceStart || !maintenanceEnd) return "Completa i Dati Richiesti";
      if (new Date(maintenanceEnd) <= new Date(maintenanceStart)) return "Dati Errati";
      return "";
    }
    if (overlayMode === "roomCleaning") {
      const cleaningStart = maintenanceDateTimeValue(draft.cleaning_date_start, draft.cleaning_time_start);
      const cleaningEnd = maintenanceDateTimeValue(draft.cleaning_date_end, draft.cleaning_time_end);
      if (!cleaningStart || !cleaningEnd || !draft.cleaning_type || !draft.cleaning_task_status || !draft.cleaning_priority) return "Completa i Dati Richiesti";
      if (new Date(cleaningEnd) <= new Date(cleaningStart)) return "Dati Errati";
      return "";
    }
    if (!payload.guest_name) return "Completa i Dati Richiesti";
    if (payload.invoice_same_as_customer === false && !payload.invoice_customer_id) return "Seleziona Intestatario Fattura";
    if (overlayMode !== "customer" && (!payload.room_number || !payload.check_in || !payload.check_out)) return "Completa i Dati Richiesti";
    if (overlayMode !== "customer" && new Date(payload.check_out) <= new Date(payload.check_in)) return "Dati Errati";
    const invalidGuest = Array.isArray(payload.guests) ? payload.guests.find((guest) => {
      if (!guest.customer_id) return true;
      if (guest.isMinor && (!guest.guardianFullName || !guest.guardianRelation)) return true;
      if (guest.isMinor && String(guest.guardianRelation || "").toLowerCase() === "accompagnatore autorizzato" && guest.guardianConsent !== true) return true;
      return false;
    }) : null;
    if (invalidGuest) return "Completa i Dati Ospiti";
    const invalidPayment = Array.isArray(payload.payments)
      ? payload.payments.find((payment) => !String(payment.paymentMethod || "").trim() || !Number.isFinite(Number(payment.amount)) || Number(payment.amount) < 0)
      : null;
    if (invalidPayment) return "Completa i Dati Pagamenti";
    return "";
  }

  async function saveOverlay() {
    if (overlayMode === "edit" && activeBookingId) updateStayTabError("booking", false);
    const payload = buildPayload();
    const validationError = validateDraft(payload);
    if (validationError) {
      setOverlayError(validationError);
      return;
    }

    if (overlayMode === "checkin") {
      const bookingValidationErrors = validateBookingRegistry(payload, {
        draft,
        customers,
        customerTypeCatalog,
        mode: "checkin",
      });
      const cardValidationError = bookingValidationErrors.credit_card_number
        || bookingValidationErrors.credit_card_circuit_id
        || bookingValidationErrors.credit_card_expiry;
      if (cardValidationError) {
        setOverlayError(cardValidationError);
        return;
      }
    }

    try {
      if (overlayMode === "roomEdit") {
        const selectedType = (roomCatalogs.roomTypes || []).find((item) => item._id === payload.room_type_id);
        const roomPayload = {
          ...draft,
          room_number: String(draft.room_number || "").trim(),
          room_type_id: draft.room_type_id,
          room_type: selectedType?.description || draft.room_type || "",
        };
        const data = activeRoomId ? await updateRoom(activeRoomId, roomPayload) : await createRoom(roomPayload);
        const updatedRoom = data?.item;
        if (updatedRoom) {
          setRooms((current) => {
            const exists = current.some((item) => String(item._id) === String(updatedRoom._id));
            return exists ? current.map((item) => String(item._id) === String(updatedRoom._id) ? updatedRoom : item) : [...current, updatedRoom];
          });
          setDraft((current) => ({ ...current, room_number: updatedRoom.room_number, room_type: updatedRoom.room_type, room_type_id: updatedRoom.room_type_id }));
        }
        closeOverlay();
        return;
      }

      if (overlayMode === "roomMaintenance") {
        const maintenancePayload = {
          date_start: maintenanceDateTimeValue(draft.maintenance_date_start, draft.maintenance_time_start),
          date_end: maintenanceDateTimeValue(draft.maintenance_date_end, draft.maintenance_time_end),
          description: String(draft.maintenance_description || "").trim(),
        };
        const data = draft.maintenance_edit_id
          ? await updateRoomMaintenance(activeRoomId, draft.maintenance_edit_id, maintenancePayload)
          : await createRoomMaintenance(activeRoomId, maintenancePayload);
        const saved = data?.item;
        if (saved) {
          setRooms((current) => current.map((item) => {
            if (String(item._id) !== String(activeRoomId)) return item;
            const previous = Array.isArray(item.maintenances) ? item.maintenances : [];
            const nextMaintenances = draft.maintenance_edit_id
              ? previous.map((row) => String(row._id) === String(saved._id) ? saved : row)
              : [...previous, saved];
            return hydrateRoomPlanningState({ ...item, maintenances: nextMaintenances });
          }));
        }
        closeOverlay();
        return;
      }

      if (overlayMode === "roomCleaning") {
        const cleaningPayload = {
          date_start: maintenanceDateTimeValue(draft.cleaning_date_start, draft.cleaning_time_start),
          date_end: maintenanceDateTimeValue(draft.cleaning_date_end, draft.cleaning_time_end),
          cleaning_type: String(draft.cleaning_type || "TURNOVER").toUpperCase(),
          status: String(draft.cleaning_task_status || "PLANNED").toUpperCase(),
          priority: String(draft.cleaning_priority || "NORMAL").toUpperCase(),
          assigned_to: String(draft.cleaning_assigned_to || "").trim(),
          blocks_room: draft.cleaning_blocks_room !== false,
          note: String(draft.cleaning_note || "").trim(),
        };
        const data = draft.cleaning_edit_id
          ? await updateRoomCleaning(activeRoomId, draft.cleaning_edit_id, cleaningPayload)
          : await createRoomCleaning(activeRoomId, cleaningPayload);
        const saved = data?.item;
        if (saved) {
          setRooms((current) => current.map((item) => {
            if (String(item._id) !== String(activeRoomId)) return item;
            const previous = Array.isArray(item.cleanings) ? item.cleanings : [];
            const nextCleanings = draft.cleaning_edit_id
              ? previous.map((row) => String(row._id) === String(saved._id) ? saved : row)
              : [...previous, saved];
            return hydrateRoomPlanningState({ ...item, cleanings: nextCleanings });
          }));
        }
        closeOverlay();
        return;
      }

      const shouldCreateBooking = overlayMode === "new" || (overlayMode === "checkin" && !activeBookingId);
      if (overlayMode !== "customer") payload.site_id = selectedSiteId;
      const data = shouldCreateBooking
        ? await createBooking(payload)
        : await updateBooking(activeBookingId, overlayMode === "customer" ? {
          guest_name: payload.guest_name,
          guest_email: payload.guest_email,
          guest_phone: payload.guest_phone,
          customer_id: payload.customer_id,
          invoice_same_as_customer: payload.invoice_same_as_customer,
          invoice_customer_id: payload.invoice_customer_id,
          invoice_name: payload.invoice_name,
          invoice_email: payload.invoice_email,
          invoice_phone: payload.invoice_phone,
          notes: payload.notes,
        } : payload);
      const updated = data?.item;
      if (updated) {
        setItems((current) => {
          const exists = current.some((item) => getBookingId(item) === getBookingId(updated));
          if (!exists) return [...current, updated];
          return current.map((item) => (getBookingId(item) === getBookingId(updated) ? updated : item));
        });
        if (payload.customer_id && overlayMode !== "customer") {
          setCustomers((current) => applyRelatedGuestLinksToCustomerPool(current, payload.customer_id, payload.relatedGuests));
        }
      }
      closeOverlay();
    } catch (err) {
      setOverlayError(err?.response?.data?.error || "Dati Errati");
    }
  }

  function editMaintenanceDraft(item) {
    setOverlayError("");
    setDraft((current) => ({
      ...current,
      maintenance_edit_id: item?._id || "",
      maintenance_date_start: maintenanceDatePart(item?.date_start),
      maintenance_time_start: maintenanceTimePart(item?.date_start),
      maintenance_date_end: maintenanceDatePart(item?.date_end),
      maintenance_time_end: maintenanceTimePart(item?.date_end),
      maintenance_description: item?.description || "",
    }));
  }

  async function deleteMaintenanceFromDraft(maintenanceId) {
    if (!activeRoomId || !maintenanceId) return;
    setOverlayError("");
    try {
      await deleteRoomMaintenance(activeRoomId, maintenanceId);
      setRooms((current) => current.map((item) => {
        if (String(item._id) !== String(activeRoomId)) return item;
        const nextMaintenances = (item.maintenances || []).filter((row) => String(row._id) !== String(maintenanceId));
        return hydrateRoomPlanningState({ ...item, maintenances: nextMaintenances });
      }));
      setDraft((current) => ({
        ...current,
        maintenances: (current.maintenances || []).filter((row) => String(row._id) !== String(maintenanceId)),
        maintenance_edit_id: current.maintenance_edit_id === maintenanceId ? "" : current.maintenance_edit_id,
        maintenance_date_start: current.maintenance_edit_id === maintenanceId ? "" : current.maintenance_date_start,
        maintenance_time_start: current.maintenance_edit_id === maintenanceId ? "" : current.maintenance_time_start,
        maintenance_date_end: current.maintenance_edit_id === maintenanceId ? "" : current.maintenance_date_end,
        maintenance_time_end: current.maintenance_edit_id === maintenanceId ? "" : current.maintenance_time_end,
        maintenance_description: current.maintenance_edit_id === maintenanceId ? "" : current.maintenance_description,
      }));
    } catch {
      setOverlayError("Eliminazione manutenzione non riuscita");
    }
  }

  function editCleaningDraft(item) {
    setOverlayError("");
    setDraft((current) => ({
      ...current,
      cleaning_edit_id: item?._id || "",
      cleaning_date_start: maintenanceDatePart(item?.date_start),
      cleaning_time_start: maintenanceTimePart(item?.date_start),
      cleaning_date_end: maintenanceDatePart(item?.date_end),
      cleaning_time_end: maintenanceTimePart(item?.date_end),
      cleaning_type: item?.cleaning_type || "TURNOVER",
      cleaning_task_status: item?.status || "PLANNED",
      cleaning_priority: item?.priority || "NORMAL",
      cleaning_assigned_to: item?.assigned_to || "",
      cleaning_blocks_room: item?.blocks_room !== false,
      cleaning_note: item?.note || "",
    }));
  }

  async function deleteCleaningFromDraft(cleaningId) {
    if (!activeRoomId || !cleaningId) return;
    setOverlayError("");
    try {
      await deleteRoomCleaning(activeRoomId, cleaningId);
      setRooms((current) => current.map((item) => {
        if (String(item._id) !== String(activeRoomId)) return item;
        const nextCleanings = (item.cleanings || []).filter((row) => String(row._id) !== String(cleaningId));
        return hydrateRoomPlanningState({ ...item, cleanings: nextCleanings });
      }));
      setDraft((current) => ({
        ...current,
        cleanings: (current.cleanings || []).filter((row) => String(row._id) !== String(cleaningId)),
        ...(String(current.cleaning_edit_id || "") === String(cleaningId) ? { cleaning_edit_id: "", cleaning_date_start: "", cleaning_time_start: "", cleaning_date_end: "", cleaning_time_end: "", cleaning_note: "" } : {}),
      }));
    } catch {
      setOverlayError("Eliminazione pulizia non riuscita");
    }
  }

  function handleContextMenu(event, booking) {
    event.preventDefault();
    event.stopPropagation();
    setActiveBookingId(getBookingId(booking));
    if (!canEditBooking && planningMode !== "HALF") {
      showUnauthorizedPlanningLabel(event.clientX, event.clientY);
      return;
    }
    const bookingRect = event.currentTarget?.getBoundingClientRect?.();
    setMenu({ x: event.clientX, y: event.clientY, anchorTop: bookingRect?.top ?? event.clientY, type: "booking", day: getClickedPlanningDay(event) });
  }

  function requestDeleteActive() {
    const booking = selectedBooking();
    if (!canDeleteBooking || normalizeStatus(booking?.status) === "CHECKED_OUT") return;
    setMenu((current) => current?.type === "booking" ? { ...current, deleteConfirm: true } : current);
  }

  function cancelDeleteActive() {
    setMenu((current) => current?.type === "booking" ? { ...current, deleteConfirm: false } : current);
  }

  async function handleDeleteActive() {
    const id = activeBookingId;
    if (!id) return;
    const booking = selectedBooking();
    if (!canDeleteBooking || normalizeStatus(booking?.status) === "CHECKED_OUT") {
      return;
    }
    setMenu(null);
    try {
      await deleteBooking(id);
      setItems((current) => current.filter((item) => getBookingId(item) !== id));
    } catch {
      showTemporaryPlanningError("Eliminazione prenotazione non riuscita");
    }
  }

  async function handleConfirmActiveBooking() {
    const booking = selectedBooking();
    if (!booking || normalizeStatus(booking.status) !== "PENDING") return;

    setMenu(null);
    setError("");
    try {
      const data = await confirmBooking(getBookingId(booking));
      const updated = data?.item;
      if (!updated) return;
      setItems((current) => current.map((item) => getBookingId(item) === getBookingId(updated) ? updated : item));
    } catch (err) {
      showTemporaryPlanningError(err?.response?.data?.error || "Conferma prenotazione non riuscita");
    }
  }

  async function openFastCheckout(booking, initialTab = "checkout") {
    ensureStayTabErrorBooking(booking);
    setMenu(null);
    setFastCheckoutError("");
    if (!booking?._id) {
      showTemporaryPlanningError("Prenotazione non valida");
      return;
    }

    fastCheckoutRequestRef.current = String(booking._id);
    try {
      const account = await fetchFreshBookingAccount(booking);
      if (fastCheckoutRequestRef.current !== String(booking._id)) return;
      setFastServiceCatalog([]);
      setFastCheckoutBooking(account);
      setFastStayTab(initialTab);
      setFastBookingDraft(bookingToDraft(account));
      setFastServiceDraft(buildFastServiceDraft(account));
      setFastServiceError("");
      // Il catalogo servizi non deve rallentare l'apertura del Fast Check-out.
      getServiceCatalog().then((data) => {
        if (fastCheckoutRequestRef.current !== String(booking._id)) return;
        setFastServiceCatalog(Array.isArray(data?.items) ? data.items : []);
      }).catch(() => {
        if (fastCheckoutRequestRef.current === String(booking._id)) setFastServiceError("Catalogo servizi non disponibile");
      });
      setFastCheckoutDraft(buildCheckoutDraft(account));
      setOverlayMode("");
    } catch (err) {
      showTemporaryPlanningError(err?.response?.data?.error || err?.message || "Dati check out non disponibili");
    }
  }

  const updateFastCheckoutDraft = useCallback((patch) => {
    setFastCheckoutDraft((current) => ({ ...current, ...patch }));
    if (Object.prototype.hasOwnProperty.call(patch || {}, "payments")) {
      const payments = normalizeBookingPayments(patch.payments);
      setFastCheckoutBooking((current) => current ? { ...current, payments } : current);
      setFastBookingDraft((current) => current ? { ...current, payments } : current);
    }
  }, []);

  function closeFastStayFlow() {
    fastCheckoutRequestRef.current = "";
    setFastCheckoutBooking(null);
    setFastStayTab("checkout");
    setFastBookingDraft(null);
    setFastServiceDraft({ stay: null, rows: [], removedIds: [] });
    setFastServiceError("");
    setFastCheckoutDraft({ ...EMPTY_CHECKOUT_DRAFT });
    setFastCheckoutError("");
    stayTabErrorBookingIdRef.current = "";
    setStayTabErrors({ booking: false, services: false, checkout: false });
  }

  function changeFastStayTab(tab) {
    if (!fastCheckoutBooking?._id) return;
    setFastStayTab(tab);
  }

  function updateFastBookingDraft(field, value) {
    setFastBookingDraft((current) => ({ ...(current || {}), [field]: value }));
    if (field === "payments") {
      const payments = normalizeBookingPayments(value);
      setFastCheckoutDraft((current) => ({ ...current, payments }));
      setFastCheckoutBooking((current) => current ? { ...current, payments } : current);
      return;
    }
    if (["check_in", "check_out", "room_number", "guest_name", "guest_email", "guest_phone", "payment_method"].includes(field)) {
      setFastCheckoutBooking((current) => current ? { ...current, [field]: value } : current);
    }
  }

  function selectFastBookingCustomer(target, customer) {
    if (!customer) return;
    if (target !== "invoice" && getCustomerRegistryTypeFromCustomer(customer, customerTypeCatalog)?.code !== "PRIVATO") return;
    const customerId = String(customer._id || customer.id || "");
    const customerName = customerDisplayName(customer);
    const customerEmail = customerContactValue(customer, ["Email"]);
    const customerPhone = customerPhoneMobileValue(customer);
    if (target === "invoice") {
      setFastBookingDraft((current) => ({ ...(current || {}), invoice_customer_id: customerId, invoice_name: customerName, invoice_email: customerEmail, invoice_phone: customerPhone }));
      return;
    }
    const customerPool = customers.some((item) => String(item._id) === customerId) ? customers : [...customers, customer];
    const initialRelatedGuests = buildRelatedGuestRows(customer, [], customerPool, 0, customerTypeCatalog);
    setFastBookingDraft((current) => ({
      ...(current || {}),
      customer_id: customerId, guest_name: customerName, guest_email: customerEmail, guest_phone: customerPhone, relatedGuests: initialRelatedGuests,
      ...(current?.invoice_same_as_customer !== false ? { invoice_customer_id: customerId, invoice_name: customerName, invoice_email: customerEmail, invoice_phone: customerPhone } : {}),
    }));
  }

  async function saveFastBookingTab() {
    if (!fastCheckoutBooking?._id || !fastBookingDraft) return;
    updateStayTabError("booking", false);
    setFastCheckoutError("");
    const checkIn = toCheckInDateTimeFromInput(fastBookingDraft.check_in, fastBookingDraft.check_in_time);
    const checkOut = toCheckOutDateTimeFromInput(fastBookingDraft.check_out, fastBookingDraft.check_out_time);
    if (!checkIn || !checkOut || new Date(checkOut) <= new Date(checkIn)) {
      setFastCheckoutError("Dati Errati");
      return;
    }
    try {
      await updateBooking(fastCheckoutBooking._id, { ...fastBookingDraft, check_in: checkIn, check_out: checkOut });
      const fresh = await fetchFreshBookingAccount(fastCheckoutBooking);
      setFastCheckoutBooking(fresh);
      setFastBookingDraft(bookingToDraft(fresh));
      setFastServiceDraft(buildFastServiceDraft(fresh));
      setFastCheckoutDraft(buildCheckoutDraft(fresh));
      closeFastStayFlow();
      await refreshPlanningSilently();
    } catch (err) {
      setFastCheckoutError(err?.response?.data?.error || "Impossibile salvare la prenotazione");
    }
  }

  function updateFastServiceDraft(patch) {
    setFastServiceDraft((current) => {
      const next = { ...current, ...patch };
      setFastCheckoutBooking((booking) => booking?._id ? mergeServiceDraftIntoBookingAccount(booking, next) : booking);
      return next;
    });
  }

  async function updateFastServiceBookingDate(field, value) {
    if (!fastCheckoutBooking?._id || !value) return;
    const currentClock = valueToDateTimeInput(fastCheckoutBooking[field])?.slice(11, 16) || (field === "check_in" ? "12:00" : "11:59");
    const dateTimeValue = field === "check_in"
      ? toCheckInDateTimeFromInput(value, currentClock)
      : toCheckOutDateTimeFromInput(value, currentClock);
    if (!dateTimeValue) return;
    const nextBooking = { ...fastCheckoutBooking, [field]: dateTimeValue };
    const nights = fastStayNights(nextBooking.check_in, nextBooking.check_out);
    if (new Date(nextBooking.check_out) <= new Date(nextBooking.check_in)) {
      setFastServiceError("Check Out deve essere successivo al Check In");
      return;
    }
    setFastServiceError("");
    setFastCheckoutBooking(nextBooking);
    setFastBookingDraft((current) => current ? { ...current, [field]: value, [`${field}_time`]: currentClock } : current);
    setFastServiceDraft((current) => ({ ...current, stay: { ...(current.stay || {}), quantity: nights } }));
    try {
      await updateBooking(fastCheckoutBooking._id, { [field]: dateTimeValue });
    } catch (err) {
      setFastServiceError(err?.response?.data?.error || "Impossibile aggiornare le date prenotazione");
    }
  }

  async function persistFastServices() {
    if (!fastCheckoutBooking?._id) return null;
    setFastServiceError("");
    const rows = Array.isArray(fastServiceDraft?.rows) ? fastServiceDraft.rows : [];
    const stay = fastServiceDraft?.stay || {};
    const nights = fastStayNights(fastCheckoutBooking.check_in, fastCheckoutBooking.check_out);
    const roomRate = Math.max(0, Number(stay.unitPrice || 0));
    const roomDiscountRate = Math.min(100, Math.max(0, Number(stay.discountRate || 0)));
    await updateBooking(fastCheckoutBooking._id, {
      room_rate: roomRate,
      room_discount_rate: roomDiscountRate,
      room_vat_rate: Math.min(100, Math.max(0, Number(stay.vatRate || 0))),
      room_charge_date: stay.chargeDate || fastCheckoutBooking.check_in,
      room_note: stay.note || "",
      total_amount: nights * roomRate * (1 - roomDiscountRate / 100),
    });
    for (const serviceId of (fastServiceDraft.removedIds || [])) await deleteBookingAccountService(fastCheckoutBooking._id, serviceId);
    for (const row of rows) {
      const payload = {
        service_id: row.service_id, description: row.description, chargeType: row.chargeType, entrySource: row.entrySource || "MANUAL_DESKTOP",
        accumulationItems: Array.isArray(row.accumulationItems) ? row.accumulationItems : [], quantity: row.quantity, unitPrice: row.unitPrice,
        discountRate: row.discountRate, vatRate: row.vatRate, chargeDate: row.chargeDate, note: row.note,
      };
      if (row._id) await updateBookingAccountService(fastCheckoutBooking._id, row._id, payload);
      else await createBookingAccountService(fastCheckoutBooking._id, payload);
    }
    const fresh = await fetchFreshBookingAccount({ ...fastCheckoutBooking, status: "CHECKED_IN" });
    const liveFresh = { ...fresh, payments: normalizeBookingPayments(fastCheckoutDraft.payments) };
    setFastCheckoutBooking(liveFresh);
    setFastServiceDraft(buildFastServiceDraft(liveFresh));
    return liveFresh;
  }

  async function saveFastServices() {
    updateStayTabError("services", false);
    try {
      await persistFastServices();
      closeFastStayFlow();
      await refreshPlanningSilently();
    } catch (err) {
      setFastServiceError(err?.response?.data?.error || "Errore salvataggio servizi");
    }
  }

  async function saveFastCheckout() {
    updateStayTabError("checkout", false);
    setFastCheckoutError("");
    if (!fastCheckoutBooking?._id) {
      setFastCheckoutError("Prenotazione non valida");
      return false;
    }
    if (!fastCheckoutDraft.emissionDate) {
      setFastCheckoutError("Data Emissione obbligatoria");
      return false;
    }
    if (!/^\d+$/.test(String(fastCheckoutDraft.documentNumber || "")) || Number(fastCheckoutDraft.documentNumber) <= 0) {
      setFastCheckoutError("Numero Documento obbligatorio e composto solo da numeri");
      return false;
    }

    const payments = normalizeBookingPayments(fastCheckoutDraft.payments);
    if (payments.some((payment) => !String(payment.paymentMethod || "").trim() || !Number.isFinite(Number(payment.amount)) || Number(payment.amount) < 0)) {
      setFastCheckoutError("Completa i Dati Pagamenti");
      return false;
    }
    if (payments.some((payment) => payment.paymentType === "ADVANCE" && Number(payment.amount || 0) > 0 && payment?.advanceDocument?.issued !== true)) {
      setFastCheckoutError("Emetti il documento fiscale per tutti gli Acconti prima del Fast Check Out");
      return false;
    }

    setFastCheckoutSaving(true);
    try {
      if (fastServiceDraft?.stay) await persistFastServices();
      if (fastBookingDraft) {
        const fastPayments = normalizeBookingPayments(fastCheckoutDraft.payments);
        const { total_amount: _ignoredTotalAmount, room_rate: _ignoredRoomRate, room_discount_rate: _ignoredRoomDiscountRate, room_vat_rate: _ignoredRoomVatRate, room_charge_date: _ignoredRoomChargeDate, room_note: _ignoredRoomNote, ...bookingDraftForSave } = fastBookingDraft;
        const checkIn = toCheckInDateTimeFromInput(fastBookingDraft.check_in, fastBookingDraft.check_in_time);
        const checkOut = toCheckOutDateTimeFromInput(fastBookingDraft.check_out, fastBookingDraft.check_out_time);
        if (!checkIn || !checkOut || new Date(checkOut) <= new Date(checkIn)) {
          setFastCheckoutError("Dati Errati");
          return false;
        }
        await updateBooking(fastCheckoutBooking._id, { ...bookingDraftForSave, check_in: checkIn, check_out: checkOut, payments: fastPayments, deposit_amount: fastPayments.filter((payment) => ["ADVANCE", "DEPOSIT"].includes(payment.paymentType)).reduce((sum, payment) => sum + Number(payment.amount || 0), 0) });
      }
      const result = await issueBookingCheckoutDocument(fastCheckoutBooking._id, {
        documentId: fastCheckoutDraft.documentId || undefined,
        documentType: fastCheckoutDraft.documentType,
        numberingMode: fastCheckoutDraft.numberingMode,
        documentNumber: fastCheckoutDraft.documentNumber,
        emissionDate: fastCheckoutDraft.emissionDate,
        orientation: "PORTRAIT",
        unconditionalDiscountAmount: fastCheckoutDraft.unconditionalDiscountAmount,
        unconditionalDiscountRate: fastCheckoutDraft.unconditionalDiscountRate,
        roundingAmount: fastCheckoutDraft.roundingAmount,
        payments,
        autoBalancePayment: fastCheckoutBooking.default_auto_balance_fast_checkout !== false,
        note: fastCheckoutDraft.note,
      });
      const issued = result?.item || {};
      const issuedPayments = Array.isArray(result?.payments) ? normalizeBookingPayments(result.payments) : payments;
      const updatedBooking = { ...fastCheckoutBooking, status: "CHECKED_OUT", latest_fiscal_document: issued, payments: issuedPayments };
      setFastCheckoutBooking(updatedBooking);
      setFastBookingDraft((current) => current ? { ...current, status: "CHECKED_OUT", _originalStatus: "CHECKED_OUT", payments: issuedPayments } : current);
      setFastCheckoutDraft((current) => ({
        ...current,
        documentId: issued._id || current.documentId,
        documentNumber: String(issued.documentSequence || issued.documentNumber || current.documentNumber || "").replace(/\D/g, "") || current.documentNumber,
        payments: issuedPayments,
      }));
      setItems((current) => current.map((item) => (
        getBookingId(item) === String(fastCheckoutBooking._id)
          ? { ...item, status: "CHECKED_OUT" }
          : item
      )));
      await refreshPlanningSilently();
      return true;
    } catch (err) {
      setFastCheckoutError(err?.response?.data?.error || "Errore emissione documento");
      return false;
    } finally {
      setFastCheckoutSaving(false);
    }
  }

  useEffect(() => {
    if (!["new", "edit", "checkin"].includes(overlayMode)) return;

    setDraft((current) => {
      const occupancy = deriveBookingOccupancyCounts(current, customers);
      const currentAdults = Number(current.adults || 0);
      const currentChildren = Number(current.children || 0);
      const currentAnimals = Number(current.animals || 0);

      if (
        currentAdults === occupancy.adults
        && currentChildren === occupancy.children
        && currentAnimals === occupancy.animals
      ) {
        return current;
      }

      return {
        ...current,
        adults: occupancy.adults,
        children: occupancy.children,
        animals: occupancy.animals,
      };
    });
  }, [overlayMode, customers, draft.customer_id, draft.guest_name, draft.check_in, draft.relatedGuests]);

  useEffect(() => {
    document.querySelectorAll(".booking_bar[data-start][data-span], .booking_maintenance_bar[data-start][data-span], .booking_drop_preview[data-start][data-span]").forEach((node) => {
      const start = Number(node.getAttribute("data-start") || 0);
      const span = Number(node.getAttribute("data-span") || 1);
      node.style.setProperty("--booking-left", `${start * planningSlotWidth + 4}px`);
      node.style.setProperty("--booking-width", `${Math.max(8, span * planningSlotWidth - 8)}px`);
    });
    document.querySelectorAll(".booking_cleaning_bar[data-start][data-span]").forEach((node) => {
      const start = Number(node.getAttribute("data-start") || 0);
      const span = Number(node.getAttribute("data-span") || 1);
      node.style.setProperty("--booking-left", `${start * planningSlotWidth}px`);
      node.style.setProperty("--booking-width", `${Math.max(1, span * planningSlotWidth)}px`);
    });
  }, [filteredItems, rooms, periodStart, dropPreview, planningSlotWidth, planningMode]);

  useEffect(() => {
    if (!menu || !menuRef.current) return;
    const node = menuRef.current;
    const rect = node.getBoundingClientRect();
    const left = Math.max(8, Math.min(Number(menu.x || 0), window.innerWidth - rect.width - 8));
    const top = Math.max(8, Number(menu.anchorTop ?? menu.y ?? 0) - rect.height - 8);
    node.style.setProperty("--booking-menu-left", `${left}px`);
    node.style.setProperty("--booking-menu-top", `${top}px`);
  }, [menu]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menu]);

  useEffect(() => {
    if (!roomTypeFilterOpen) return undefined;
    const closeRoomTypeFilter = (event) => {
      if (roomTypeFilterRef.current?.contains(event.target)) return;
      setRoomTypeFilterOpen(false);
    };
    window.addEventListener("pointerdown", closeRoomTypeFilter, true);
    window.addEventListener("scroll", closeRoomTypeFilter, true);
    return () => {
      window.removeEventListener("pointerdown", closeRoomTypeFilter, true);
      window.removeEventListener("scroll", closeRoomTypeFilter, true);
    };
  }, [roomTypeFilterOpen]);

  useEffect(() => {
    if (!statusFilterOpen) return undefined;
    const closeStatusFilter = (event) => {
      if (statusFilterRef.current?.contains(event.target)) return;
      setStatusFilterOpen(false);
    };
    window.addEventListener("pointerdown", closeStatusFilter, true);
    window.addEventListener("scroll", closeStatusFilter, true);
    return () => {
      window.removeEventListener("pointerdown", closeStatusFilter, true);
      window.removeEventListener("scroll", closeStatusFilter, true);
    };
  }, [statusFilterOpen]);


  function chooseRoomTypeFilter(nextValue) {
    setRoomTypeFilter(nextValue);
    setRoomTypeFilterOpen(false);
  }

  function handleRoomTypeFilterTypeAhead(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const targetIsOption = event.target?.closest?.(".booking_status_option");
    if (targetIsOption && (event.key === "Enter" || event.key === " ")) return;
    if (event.key === "Tab") {
      setRoomTypeFilterOpen(false);
      return;
    }
    const now = Date.now();
    const typeBufferActive = now - roomTypeFilterTypeBufferTimeRef.current <= 850 && roomTypeFilterTypeBufferRef.current.length > 0;
    if (event.key === "Enter" || (event.key === " " && !roomTypeFilterOpen && !typeBufferActive)) {
      event.preventDefault();
      setStatusFilterOpen(false);
      setRoomTypeFilterOpen((current) => !current);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setStatusFilterOpen(false);
      setRoomTypeFilterOpen(true);
      return;
    }
    if (event.key !== "Backspace" && event.key.length !== 1) return;

    const expired = now - roomTypeFilterTypeBufferTimeRef.current > 850;
    roomTypeFilterTypeBufferTimeRef.current = now;
    roomTypeFilterTypeBufferRef.current = event.key === "Backspace"
      ? (expired ? "" : roomTypeFilterTypeBufferRef.current.slice(0, -1))
      : `${expired ? "" : roomTypeFilterTypeBufferRef.current}${event.key}`;

    if (event.key === " ") event.preventDefault();
    const typed = roomTypeFilterTypeBufferRef.current.trim().toLocaleLowerCase("it");
    if (!typed) return;
    const match = roomTypeFilterOptions.find((option) => String(option.label || "").trim().toLocaleLowerCase("it").startsWith(typed));
    if (!match) return;
    event.preventDefault();
    setRoomTypeFilter(match.value);
  }

  function chooseStatusFilter(nextValue) {
    setStatusFilter(nextValue);
    setStatusFilterOpen(false);
  }

  function handleStatusFilterTypeAhead(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const targetIsOption = event.target?.closest?.(".booking_status_option");
    if (targetIsOption && (event.key === "Enter" || event.key === " ")) return;
    if (event.key === "Tab") {
      setStatusFilterOpen(false);
      return;
    }
    const now = Date.now();
    const typeBufferActive = now - statusFilterTypeBufferTimeRef.current <= 850 && statusFilterTypeBufferRef.current.length > 0;
    if (event.key === "Enter" || (event.key === " " && !statusFilterOpen && !typeBufferActive)) {
      event.preventDefault();
      setStatusFilterOpen((current) => !current);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setStatusFilterOpen(true);
      return;
    }
    if (event.key !== "Backspace" && event.key.length !== 1) return;

    const expired = now - statusFilterTypeBufferTimeRef.current > 850;
    statusFilterTypeBufferTimeRef.current = now;
    statusFilterTypeBufferRef.current = event.key === "Backspace"
      ? (expired ? "" : statusFilterTypeBufferRef.current.slice(0, -1))
      : `${expired ? "" : statusFilterTypeBufferRef.current}${event.key}`;

    if (event.key === " ") event.preventDefault();
    const typed = statusFilterTypeBufferRef.current.trim().toLocaleLowerCase("it");
    if (!typed) return;
    const match = STATUS_FILTER_OPTIONS.find((option) => String(option.label || "").trim().toLocaleLowerCase("it").startsWith(typed));
    if (!match) return;
    event.preventDefault();
    setStatusFilter(match.value);
  }

  const hasOperationalPlanningItems = rooms.some((room) => maintenancesForRoom(room.room_number).length > 0 || cleaningsForRoom(room.room_number).length > 0);

  const contextBooking = selectedBooking();
  const contextBookingStatus = contextBooking ? normalizeStatus(contextBooking.status) : "";
  const canFastCheckIn = ["PENDING", "CONFIRMED"].includes(contextBookingStatus);
  const canConfirmBooking = contextBookingStatus === "PENDING";
  const canFastCheckOut = contextBookingStatus === "CHECKED_IN";
  const contextBookingConcluded = contextBookingStatus === "CHECKED_OUT";
  const concludedDeleteTooltip = "Non puoi eliminare la prenotazione perché è fatturata.";
  const planningTableMessage = loading
    ? "Caricamento Planning"
    : error
      ? error
      : filteredItems.length === 0 && !hasOperationalPlanningItems
        ? "Nessun Risultato Trovato"
        : "";

  return (
    <section className={`window_page bookings_page${loading ? " is_loading" : ""}`}>
      <div className="window_banner">
        <div className="window_banner_copy">
          <h1>PLANNING</h1>
          <p>{planningMode === "HOUR" ? "Planning orario su tre giorni con prenotazioni, manutenzioni e pulizie." : "Planning camere su tre mesi con stati, disponibilità e spostamento rapido delle prenotazioni."}</p>
        </div>
      </div>

      <div className={`booking_planning_workspace${planningFullscreen ? " is_fullscreen" : ""}${planningMode === "HOUR" ? " is_hourly" : ""}`}>
        {planningFullscreen && (
          <div className="booking_planning_fullscreen_header">
            <button type="button" className="booking_planning_fullscreen_close" onClick={() => setPlanningFullscreen(false)} aria-label="Chiudi planning a schermo intero">×</button>
          </div>
        )}
      <div className="booking_stats_strip" aria-label="Riepilogo periodo visualizzato">
        <article className="booking_stats_intro">
          <span>Periodo visualizzato</span>
          <strong>{planningMode === "HOUR" ? `${formatDateForInput(days[0])} → ${formatDateForInput(days[2])}` : formatRangeMonth(periodStart)}</strong>
        </article>
        <article><span>{planningMode === "HOUR" ? "Prenotazioni 3 giorni" : "Prenotazioni mese"}</span><strong>{stats.visible}</strong><small>Dopo il filtro stato</small></article>
        <article><span>{planningMode === "HOUR" ? "Ore occupate" : "Notti occupate"}</span><strong>{stats.occupied}</strong><small>{planningMode === "HOUR" ? "Tre giorni visualizzati" : "Solo mese selezionato"}</small></article>
        <article><span>Ricavi previsti</span><strong>{money(stats.revenue)}</strong><small>{planningMode === "HOUR" ? "Tre giorni visualizzati" : "Solo mese selezionato"}</small></article>
        <article><span>Da confermare</span><strong>{stats.pending}</strong><small>Prenotazioni in attesa</small></article>
      </div>

      <div className="booking_planning_card">
      <div className="booking_toolbar">
        <div className="booking_toolbar_spacer">
          <button type="button" className="booking_action_button" onClick={handleTodayClick}>Oggi</button>
          {!planningFullscreen && (
            <button type="button" className="booking_planning_fullscreen_toggle" onClick={() => setPlanningFullscreen(true)} aria-label="Espandi planning a schermo intero" title="Espandi planning">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" /></svg>
            </button>
          )}
          <button type="button" className={`booking_planning_refresh_button${manualRefreshing ? " is_refreshing" : ""}`} disabled={manualRefreshing} onClick={() => { void refreshPlanningSilently(true); }} aria-label="Aggiorna planning" title="Aggiorna planning" aria-busy={manualRefreshing}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6v5h-5M4 18v-5h5M6.1 9a7 7 0 0 1 11.8-2.5L20 11M4 13l2.1 4.5A7 7 0 0 0 17.9 15" /></svg>
          </button>
          <VestaSelect className="booking_planning_site_select" aria-label="Sede del planning" value={selectedSiteId} onChange={(event) => {
            setSelectedSiteId(event.target.value);
            setItems([]);
            setRooms([]);
            setRoomTypeFilter("ALL");
            setMenu(null);
          }} disabled={!sites.length}>
            {!sites.length && <option value="">Nessuna sede</option>}
            {sites.map((site) => <option key={site._id} value={site._id}>{site.description}</option>)}
          </VestaSelect>
        </div>

        <div className="booking_period_controls" aria-label={planningMode === "HOUR" ? "Selezione tre giorni planning orario" : "Selezione trimestre planning"}>
          <button type="button" className="booking_nav_button" onClick={() => setPeriodStart((d) => planningMode === "HOUR" ? addDays(d, -1) : (d.getDate() === 1 ? addMonths(d, -1) : new Date(d.getFullYear(), d.getMonth(), 1)))}>‹</button>
          <div className="booking_period_box">
            {planningMode === "HOUR" ? (
              <><strong>{formatDateForInput(days[0])}</strong><span>{formatDateForInput(days[1])} · {formatDateForInput(days[2])}</span></>
            ) : (
              <><strong>{formatRangeMonth(periodStart)}</strong><span>{formatRangeMonth(addMonths(periodStart, 1))} · {formatRangeMonth(addMonths(periodStart, 2))}</span></>
            )}
          </div>
          <button type="button" className="booking_nav_button" onClick={() => setPeriodStart((d) => planningMode === "HOUR" ? addDays(d, 1) : addMonths(d, 1))}>›</button>
        </div>

        <div className="booking_actions">
          <div className="booking_status_dropdown booking_room_type_dropdown" ref={roomTypeFilterRef}>
            <button
              type="button"
              className="booking_status_trigger booking_room_type_trigger"
              aria-haspopup="listbox"
              aria-expanded={roomTypeFilterOpen}
              onClick={() => {
                setStatusFilterOpen(false);
                setRoomTypeFilterOpen((current) => !current);
              }}
              onKeyDown={handleRoomTypeFilterTypeAhead}
            >
              <RoomTypeIcon label={activeRoomTypeOption.label} value={activeRoomTypeOption.value} />
              <span>{activeRoomTypeOption.label}</span>
              <strong>{activeRoomTypeOption.count || 0}</strong>
              <span className="booking_status_chevron" aria-hidden="true"></span>
            </button>
            {roomTypeFilterOpen && (
              <div className="booking_status_menu" role="listbox" aria-label="Filtro tipologia camere" onKeyDown={handleRoomTypeFilterTypeAhead}>
                <div className="booking_status_menu_scroll">
                  {roomTypeFilterOptions.map((option) => {
                    const isActive = option.value === roomTypeFilter;
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`booking_status_option ${isActive ? "is_active" : ""}`}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => chooseRoomTypeFilter(option.value)}
                      >
                        <RoomTypeIcon label={option.label} value={option.value} />
                        <span>{option.label}</span>
                        <strong>{option.count || 0}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="booking_status_dropdown" ref={statusFilterRef}>
            <button
              type="button"
              className="booking_status_trigger"
              aria-haspopup="listbox"
              aria-expanded={statusFilterOpen}
              onClick={() => {
                setRoomTypeFilterOpen(false);
                setStatusFilterOpen((current) => !current);
              }}
              onKeyDown={handleStatusFilterTypeAhead}
            >
              {statusFilter === "ALL" ? (
                <span className="booking_status_dot_stack" aria-hidden="true">
                  {STATUS_ALL_DOT_ORDER.map((status, index) => (
                    <span key={`${status}_${index}`} className={`booking_status_dot status_${status.toLowerCase()}`} />
                  ))}
                </span>
              ) : (
                <span className={`booking_status_dot status_${statusFilter.toLowerCase()}`} aria-hidden="true" />
              )}
              <span>{activeStatusOption.label}</span>
              <strong>{statusFilter === "ALL" ? allStatusVisibleItems.length : planningStatusCounts[statusFilter] || 0}</strong>
              <span className="booking_status_chevron" aria-hidden="true"></span>
            </button>
            {statusFilterOpen && (
              <div className="booking_status_menu" role="listbox" aria-label="Filtro stato prenotazioni" onKeyDown={handleStatusFilterTypeAhead}>
                <div className="booking_status_menu_scroll">
                  {STATUS_FILTER_OPTIONS.map((option) => {
                    const count = option.value === "ALL" ? allStatusVisibleItems.length : planningStatusCounts[option.value] || 0;
                    const isActive = option.value === statusFilter;
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`booking_status_option ${isActive ? "is_active" : ""}`}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => chooseStatusFilter(option.value)}
                      >
                        {option.value === "ALL" ? (
                          <span className="booking_status_dot_stack" aria-hidden="true">
                            {STATUS_ALL_DOT_ORDER.map((status, index) => (
                              <span key={`${status}_${index}`} className={`booking_status_dot status_${status.toLowerCase()}`} />
                            ))}
                          </span>
                        ) : (
                          <span className={`booking_status_dot status_${option.value.toLowerCase()}`} aria-hidden="true" />
                        )}
                        <span>{option.label}</span>
                        <strong>{count}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="booking_planning_shell">
        {planningTableMessage && <div className={`booking_planning_visible_status${loading ? " is_loading" : ""}${!loading && error ? " is_error" : ""}${!loading && !error && filteredItems.length === 0 && !hasOperationalPlanningItems ? " is_empty" : ""}`}>{loading && <span className="booking_planning_loading_spinner" aria-hidden="true"><span /></span>}<span>{planningTableMessage}</span></div>}
        <div className="booking_planning_scroll" ref={planningScrollRef}>
          <div className="booking_planning_grid">
            <div className="booking_corner_cell">Camera / Tipologia</div>
            <div className="booking_days_header">
              {days.map((day, dayIndex) => (
                <div key={toIsoDate(day)} className={`booking_day_cell${planningMode === "HOUR" ? " is_hourly" : ""} ${[0, 6].includes(day.getDay()) ? "is_weekend" : ""} ${hoverCell?.dayIndex === dayIndex ? "is_crosshair" : ""}`}>
                  <div className="booking_day_heading"><span>{WEEKDAYS[day.getDay()]}</span><strong>{day.getDate()}</strong></div>
                  {planningMode === "HOUR" && <div className="booking_hour_labels">{Array.from({ length: 24 }, (_, hour) => <span key={`${toIsoDate(day)}-${hour}`}>{String(hour).padStart(2, "0")}</span>)}</div>}
                </div>
              ))}
            </div>

            <div className="booking_rooms_column">
              {groups.map((group) => (
                <div key={group.type} className="booking_group_block">
                  <div className="booking_group_title">⌄ {group.type}</div>
                  {group.rooms.map((room) => (
                    <div
                      key={`${group.type}-${room}`}
                      className={`booking_room_cell ${hoverCell?.room === String(room) ? "is_crosshair" : ""}`}
                      onContextMenu={(event) => handleRoomContextMenu(event, room, group.type)}
                    >{room}</div>
                  ))}
                </div>
              ))}
            </div>

            <div className="booking_timeline_area">
              {groups.map((group) => (
                <div key={group.type} className="booking_timeline_group">
                  <div className="booking_group_spacer" />
                  {group.rooms.map((room) => (
                    <div
                      key={`${group.type}-${room}`}
                      className={`booking_timeline_row ${hoverCell?.room === String(room) ? "is_row_crosshair" : ""}`}
                      onMouseLeave={() => setHoverCell(null)}
                      onDragEnter={allowDrop}
                      onDragOver={(event) => handleRowDragOver(event, room, group.type)}
                      onDrop={(event) => handleRowDrop(event, room, group.type)}
                    >
                      {days.flatMap((day, dayIndex) => planningMode === "HOUR"
                        ? Array.from({ length: 24 }, (_, hour) => {
                          const slotIndex = (dayIndex * 24) + hour;
                          return (
                            <span key={`${room}-${toIsoDate(day)}-${hour}`} className={`booking_grid_slot booking_grid_hour ${[0, 6].includes(day.getDay()) ? "is_weekend" : ""} ${hoverCell?.slotIndex === slotIndex ? "is_half_crosshair" : ""} ${hoverCell?.dayIndex === dayIndex ? "is_column_crosshair" : ""}`} title={`${toIsoDate(day)} ${String(hour).padStart(2, "0")}:00-${String(hour).padStart(2, "0")}:59`} onMouseEnter={() => setHoverCell({ room: String(room), dayIndex, slotIndex, half: "HOUR", hour })} onContextMenu={(event) => handleEmptyDayContextMenu(event, room, group.type, day, "HOUR", hour)} onDragEnter={(event) => handleCellDragOver(event, room, group.type, slotIndex)} onDragOver={(event) => handleCellDragOver(event, room, group.type, slotIndex)} onDrop={(event) => handleDrop(event, room, group.type, slotIndex)} />
                          );
                        })
                        : ["AM", "PM"].map((half, halfIndex) => {
                          const slotIndex = (dayIndex * 2) + halfIndex;
                          return (
                            <span key={`${room}-${toIsoDate(day)}-${half}`} className={`booking_grid_slot booking_grid_half ${[0, 6].includes(day.getDay()) ? "is_weekend" : ""} ${half === "AM" ? "is_am" : "is_pm"} ${hoverCell?.slotIndex === slotIndex ? "is_half_crosshair" : ""} ${hoverCell?.dayIndex === dayIndex ? "is_column_crosshair" : ""}`} title={`${toIsoDate(day)} ${half === "AM" ? "00:00-11:59" : "12:00-23:59"}`} onMouseEnter={() => setHoverCell({ room: String(room), dayIndex, slotIndex, half })} onContextMenu={(event) => handleEmptyDayContextMenu(event, room, group.type, day, half, null)} onDragEnter={(event) => handleCellDragOver(event, room, group.type, slotIndex)} onDragOver={(event) => handleCellDragOver(event, room, group.type, slotIndex)} onDrop={(event) => handleDrop(event, room, group.type, slotIndex)} />
                          );
                        }))}
                      {dropPreview && dropPreview.room === String(room) && (
                        <span
                          className={`booking_drop_preview ${dropPreview.invalid ? "is_invalid" : ""}`}
                          data-start={dropPreview.slotIndex}
                          data-span={dropPreview.span}
                          aria-hidden="true"
                        />
                      )}
                      {cleaningsForRoom(room).map((cleaning) => {
                        const position = getCleaningPosition(cleaning);
                        const status = String(cleaning.status || "PLANNED").toUpperCase();
                        return (
                          <span key={cleaning._id} className={`booking_cleaning_bar status_${status.toLowerCase()}${cleaning.blocks_room === false ? " is_non_blocking" : ""}${draggingCleaningId === String(cleaning._id || "") ? " is_dragging" : ""}${savingCleaningMoveId === String(cleaning._id || "") ? " is_saving" : ""}`} data-start={position.start} data-span={position.span} title={`${cleaning.note || "Pulizia"}${cleaning.assigned_to ? ` · ${cleaning.assigned_to}` : ""}`} draggable={canEditBooking && !savingCleaningMoveId && status !== "DONE"} onDragStart={(event) => handleCleaningDragStart(event, cleaning)} onDragEnd={handleDragEnd} onContextMenu={(event) => handleCleaningContextMenu(event, room, group.type, cleaning)}>
                            <span>{cleaning.cleaning_type === "STAYOVER" ? "Riassetto" : cleaning.cleaning_type === "DEEP" ? "Pulizia Profonda" : cleaning.cleaning_type === "INSPECTION" ? "Controllo" : "Pulizia"}</span>
                          </span>
                        );
                      })}
                      {maintenancesForRoom(room).map((maintenance) => {
                        const position = getMaintenancePosition(maintenance);
                        return (
                          <span
                            key={maintenance._id}
                            className={`booking_maintenance_bar${draggingMaintenanceId === String(maintenance._id || "") ? " is_dragging" : ""}${savingMaintenanceMoveId === String(maintenance._id || "") ? " is_saving" : ""}`}
                            data-start={position.start}
                            data-span={position.span}
                            title={`${maintenance.description || "In Manutenzione"}`}
                            draggable={canEditBooking && !savingMaintenanceMoveId}
                            onDragStart={(event) => handleMaintenanceDragStart(event, maintenance)}
                            onDragEnd={handleDragEnd}
                            onContextMenu={(event) => handleMaintenanceContextMenu(event, room, group.type, maintenance)}
                          >
                            <span>In Manutenzione</span>
                          </span>
                        );
                      })}
                      {bookingsForRoom(room).map((booking) => {
                        const status = normalizeStatus(booking.status);
                        const position = getBookingPosition(booking);
                        const bookingId = getBookingId(booking);
                        return (
                          <button
                            type="button"
                            key={bookingId}
                            className={`booking_bar status_${status.toLowerCase()} ${draggingId === bookingId ? "is_dragging" : ""} ${savingMoveId === bookingId ? "is_saving" : ""}`}
                            data-start={position.start}
                            data-span={position.span}
                            data-vesta-focus-id={bookingId}
                            draggable={!savingMoveId && canEditBooking && status !== "CHECKED_OUT"}
                            onContextMenu={(event) => handleContextMenu(event, booking)}
                            onDragStart={(event) => handleDragStart(event, booking)}
                            onDragEnd={handleDragEnd}
                            title={`${booking.guest_name} · Camera ${booking.room_number} · ${STATUS_LABELS[status] || status}`}
                          >
                            <span>{getBookingName(booking)}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
      </div>
      </div>

      {menu && menu.type === "unauthorized" && (
        <div className="booking_context_menu booking_context_unauthorized" ref={menuRef} onClick={(event) => event.stopPropagation()}>
          {UNAUTHORIZED_LABEL}
        </div>
      )}

      {menu && menu.type === "booking" && contextBooking && (
        <div className="booking_context_menu" ref={menuRef} onClick={(event) => event.stopPropagation()}>
          {canEditBooking && <button type="button" onClick={() => openOverlay("edit", contextBooking)}>Prenotazione / Soggiorno</button>}
          {canEditBooking && <button type="button" onClick={() => openBookingGuestRegistry(contextBooking)}>Anagrafica Ospite</button>}
          {canEditBooking && <button type="button" disabled={!canFastCheckIn} onClick={() => openOverlay("checkin", contextBooking)}>Fast Check In</button>}
          {canEditBooking && <button type="button" disabled={!canConfirmBooking} onClick={handleConfirmActiveBooking}>Conferma Prenotazione</button>}
          {canEditBooking && <button type="button" disabled={!canFastCheckOut} onClick={() => openFastCheckout(contextBooking)}>Fast Check Out</button>}
          {planningMode === "HALF" && menu.day && <button type="button" onClick={switchToClickedDay}>Vista GG / HH</button>}
          {planningMode === "HOUR" && <button type="button" onClick={switchToMonthDay}>Vista MM / GG</button>}
          {canEditBooking && (contextBookingConcluded ? (
            <span className="booking_disabled_delete_hint" data-tooltip={concludedDeleteTooltip}>
              <button type="button" className="danger" disabled>Elimina</button>
            </span>
          ) : menu.deleteConfirm ? (
            <div className="booking_context_delete_confirm" aria-label="Conferma eliminazione prenotazione">
              <button type="button" className="booking_context_delete_cancel" onClick={cancelDeleteActive}>Annulla</button>
              <button type="button" className="booking_context_delete_confirm_button" onClick={handleDeleteActive}>Conferma</button>
            </div>
          ) : (
            <button type="button" className={`danger${canDeleteBooking ? "" : " is_permission_blocked"}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={requestDeleteActive}>Elimina</button>
          ))}
        </div>
      )}

      {menu && menu.type === "maintenance" && (
        <div className="booking_context_menu" ref={menuRef} onClick={(event) => event.stopPropagation()}>
          {canEditBooking && <button type="button" onClick={() => openOverlay("roomMaintenance", null, { room: menu.room, roomType: menu.roomType, maintenance: menu.maintenance })}>Modifica Manutenzione</button>}
          {planningMode === "HALF" && menu.day && <button type="button" onClick={switchToClickedDay}>Vista GG / HH</button>}
          {planningMode === "HOUR" && <button type="button" onClick={switchToMonthDay}>Vista MM / GG</button>}
        </div>
      )}

      {menu && menu.type === "cleaning" && (
        <div className="booking_context_menu" ref={menuRef} onClick={(event) => event.stopPropagation()}>
          <button type="button" onClick={() => openOverlay("roomCleaning", null, { room: menu.room, roomType: menu.roomType, cleaning: menu.cleaning })}>Modifica Pulizia</button>
          {planningMode === "HOUR" && <button type="button" onClick={switchToMonthDay}>Vista MM / GG</button>}
        </div>
      )}

      {menu && menu.type === "empty" && (
        <div className="booking_context_menu" ref={menuRef} onClick={(event) => event.stopPropagation()}>
          {canEditBooking && <button type="button" onClick={() => openOverlay("new", null, getPlanningMenuContext())}>Nuova Prenotazione</button>}
          {canEditBooking && <button type="button" onClick={() => openOverlay("roomMaintenance", null, getPlanningMenuContext())}>Nuova Manutenzione</button>}
          {canEditBooking && planningMode === "HOUR" && <button type="button" onClick={() => openOverlay("roomCleaning", null, getPlanningMenuContext())}>Nuova Pulizia</button>}
          <button type="button" onClick={() => openOverlay("availability", null, getPlanningAvailabilityContext())}>Disponibilità</button>
          {planningMode === "HALF" && <button type="button" onClick={switchToClickedDay}>Vista GG / HH</button>}
          {planningMode === "HOUR" && <button type="button" onClick={switchToMonthDay}>Vista MM / GG</button>}
        </div>
      )}

      {menu && menu.type === "room" && (
        <div className="booking_context_menu" ref={menuRef} onClick={(event) => event.stopPropagation()}>
          <button type="button" onClick={() => openOverlay("roomEdit", null, getPlanningMenuContext())}>Modifica Camera</button>
          <button type="button" onClick={() => openOverlay("roomMaintenance", null, getPlanningMenuContext())}>Gestione Manutenzione</button>
          {planningMode === "HOUR" && <button type="button" onClick={() => openOverlay("roomCleaning", null, getPlanningMenuContext())}>Gestione Pulizie</button>}
          {planningMode === "HOUR" && <button type="button" onClick={switchToMonthDay}>Vista MM / GG</button>}
        </div>
      )}

      <BookingOverlay
        mode={overlayMode}
        draft={draft}
        error={overlayError}
        onChange={updateDraft}
        onClose={closeOverlay}
        onSave={saveOverlay}
        onEditMaintenance={editMaintenanceDraft}
        onDeleteMaintenance={deleteMaintenanceFromDraft}
        onEditCleaning={editCleaningDraft}
        onDeleteCleaning={deleteCleaningFromDraft}
        onCustomerAction={openCustomerRegistry}
        onCustomerSelect={selectBookingCustomer}
        roomCatalogs={roomCatalogs}
        rooms={rooms}
        sites={sites}
        creditCardCircuits={creditCardCircuits}
        paymentMethodCatalog={paymentMethodCatalog}
        customers={customers}
        customerTypeCatalog={customerTypeCatalog}
        onRoomAction={openRoomRegistryFromBooking}
        activeStayTab="booking"
        stayTabErrors={stayTabErrors}
        onStayTabErrorChange={updateStayTabError}
        locked={overlayMode === "edit" && normalizeStatus(draft.status) === "CHECKED_OUT"}
        onStayTabChange={overlayMode === "edit" ? (tab) => {
          if (tab === "booking") return;
          const currentBooking = items.find((item) => getBookingId(item) === String(activeBookingId));
          if (currentBooking) void openFastCheckout(currentBooking, tab);
        } : undefined}
      />
      {fastCheckoutBooking && fastStayTab === "booking" && fastBookingDraft && (
        <BookingOverlay
          mode="edit"
          draft={fastBookingDraft}
          error={fastCheckoutError}
          onChange={updateFastBookingDraft}
          onClose={closeFastStayFlow}
          onSave={saveFastBookingTab}
          onCustomerAction={openCustomerRegistry}
          onCustomerSelect={selectFastBookingCustomer}
          roomCatalogs={roomCatalogs}
          rooms={rooms}
          sites={sites}
          creditCardCircuits={creditCardCircuits}
          paymentMethodCatalog={paymentMethodCatalog}
          customers={customers}
          customerTypeCatalog={customerTypeCatalog}
          onRoomAction={openRoomRegistryFromBooking}
          bookingId={fastCheckoutBooking._id}
          activeStayTab={fastStayTab}
          onStayTabChange={changeFastStayTab}
          stayTabErrors={stayTabErrors}
          onStayTabErrorChange={updateStayTabError}
          locked={normalizeStatus(fastCheckoutBooking.status) === "CHECKED_OUT"}
        />
      )}
      {fastCheckoutBooking && fastStayTab === "services" && (
        <BookingServiceOverlay
          booking={fastCheckoutBooking}
          catalog={fastServiceCatalog}
          draft={fastServiceDraft}
          error={fastServiceError}
          onChange={updateFastServiceDraft}
          onBookingDateChange={updateFastServiceBookingDate}
          onClose={closeFastStayFlow}
          onSave={saveFastServices}
          activeStayTab={fastStayTab}
          onStayTabChange={changeFastStayTab}
          stayTabErrors={stayTabErrors}
          onStayTabErrorChange={updateStayTabError}
          locked={normalizeStatus(fastCheckoutBooking.status) === "CHECKED_OUT"}
        />
      )}
      {fastCheckoutBooking && fastStayTab === "checkout" && (
        <BookingCheckoutOverlay
          booking={fastCheckoutBooking}
          draft={fastCheckoutDraft}
          error={fastCheckoutError}
          saving={fastCheckoutSaving}
          paymentMethodCatalog={paymentMethodCatalog}
          onChange={updateFastCheckoutDraft}
          onClose={closeFastStayFlow}
          onSave={saveFastCheckout}
          onSaveAndClose={async () => { if (await saveFastCheckout()) closeFastStayFlow(); }}
          onBookingStateChange={(patch) => {
            setFastCheckoutBooking((current) => current ? { ...current, ...patch } : current);
            if (Array.isArray(patch?.payments)) setFastBookingDraft((current) => current ? { ...current, payments: normalizeBookingPayments(patch.payments) } : current);
            if (patch?.status) setFastBookingDraft((current) => current ? { ...current, status: patch.status, _originalStatus: patch.status } : current);
            if (patch?.status === "CHECKED_IN") {
              setItems((current) => current.map((item) => getBookingId(item) === String(fastCheckoutBooking?._id || "") ? { ...item, status: "CHECKED_IN" } : item));
              refreshPlanningSilently().catch(() => {});
            }
          }}
          activeStayTab={fastStayTab}
          onStayTabChange={changeFastStayTab}
          stayTabErrors={stayTabErrors}
          onStayTabErrorChange={updateStayTabError}
        />
      )}
      {roomOverlay && (
        <BookingOverlay
          mode="roomEdit"
          draft={roomDraft}
          error={roomError}
          onChange={updateRoomDraft}
          onClose={() => setRoomOverlay(null)}
          onSave={saveRoomRegistry}
          onEditMaintenance={editMaintenanceDraft}
          onDeleteMaintenance={deleteMaintenanceFromDraft}
          onCustomerAction={openCustomerRegistry}
          onCustomerSelect={selectBookingCustomer}
          roomCatalogs={roomCatalogs}
          rooms={rooms}
          sites={sites}
          creditCardCircuits={creditCardCircuits}
          paymentMethodCatalog={paymentMethodCatalog}
          customers={customers}
          customerTypeCatalog={customerTypeCatalog}
          onRoomAction={openRoomRegistryFromBooking}
        />
      )}
      {customerOverlay && (
        <CustomerOverlay
          draft={customerDraft}
          error={customerError}
          validationErrors={customerValidationActive ? customerValidationErrors : {}}
          onChange={updateCustomerDraft}
          onPrivacyConsentChange={updateCustomerPrivacyConsent}
          onValidationFieldFocus={clearCustomerValidationError}
          onError={setCustomerError}
          onClose={() => setCustomerOverlay(null)}
          onSave={saveCustomerRegistry}
          customerTypeOptions={customerOverlay.mode === "new" && customerOverlay.target !== "invoice"
            ? customerTypeCatalog.filter((item) => getCustomerRegistryType(item?.code || item?._id || item?.description)?.code === "PRIVATO")
            : customerTypeCatalog}
          prefixCatalog={roomCatalogs.countryPrefixes}
          roomTypeOptions={roomCatalogs.roomTypes}
          bedTypeOptions={roomCatalogs.bedTypes}
          customerFieldCatalogs={roomCatalogs}
          enableItalianVatLookup={customerOverlay.target === "invoice"}
          preferenceRequired={{ preferredLanguage: getActiveSiteDefaults(draft.room_number)?.customer?.preferredLanguage?.required === true, preferredRoomTypeId: getActiveSiteDefaults(draft.room_number)?.customer?.preferredRoomTypeId?.required === true, preferredFloor: getActiveSiteDefaults(draft.room_number)?.customer?.preferredFloor?.required === true, preferredBedTypeId: getActiveSiteDefaults(draft.room_number)?.customer?.preferredBedTypeId?.required === true, arrivalPreference: getActiveSiteDefaults(draft.room_number)?.customer?.arrivalPreference?.required === true }}
        />
      )}
    </section>
  );
}
